﻿import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, Aperture, BadgeCheck, Barcode, Bot, Braces, Camera, Captions, CircleDot, Clock3, Crop, Crosshair,
  Download, Eye, FileText, FlipHorizontal2, Focus, Folder, Gauge, Grid3X3, Hash, Heart, Image,
  ImagePlus, Layers, Loader2, Lock, MapPinned, Maximize2, Moon, Package, PenLine, QrCode,
  ReceiptText, RotateCw, ScanLine, Search, Settings, Share2, ShoppingCart, SlidersHorizontal,
  Sparkles, SunMedium, Tag, Timer, Type, Video, Volume2, Wand2, X, Zap, ZapOff
} from "lucide-react";
import { autoEnhanceBlob, captureFrame, readTrackSupport, setTorch, setZoom, startCamera, stopStream, tapToFocus, vibrate } from "../lib/camera";
import { FILTERS, applyFilterToBlob, cssFilterFromSettings, getFilterById, mergeFilterSettings } from "../lib/filters";
import { savePhoto, putRecord } from "../lib/indexedDb";
import { requestGroqAnalysis } from "../lib/aiClient";
import {
  applyWatermark, buildWatermarkText, categorizeImage,
  getActiveChecklist, getActiveProject, getWatermarkSettings,
  tickChecklistItem, addToSyncQueue,
} from "../lib/enterprise";
import { blobToDataUrl } from "../lib/exportUtils";
import FilterCarousel from "./FilterCarousel";
import MotionDetector from "./MotionDetector";

// â"€â"€â"€ helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function CameraThumbnail({ photo }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const blob = photo?.editedBlob || photo?.blob;
    if (!blob) { setUrl(""); return undefined; }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photo]);
  return url ? <img alt="" src={url} /> : <Image size={24} />;
}

function captureAssistDataUrl(video) {
  if (!video?.videoWidth || !video?.videoHeight) throw new Error("Camera preview is not ready for AI Assist yet.");
  const maxEdge = 768;
  const scale = Math.min(1, maxEdge / Math.max(video.videoWidth, video.videoHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  canvas.getContext("2d", { alpha: false }).drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.68);
}

function normalizeAssistResult(result) {
  const raw = result?.raw || {};
  const labels = raw.labels || raw.objects || raw.detected || raw.items || [];
  const clamp100 = (v) => {
    if (typeof v !== "number") return null;
    const score = v > 0 && v <= 1 ? v * 100 : v;
    return Math.max(0, Math.min(100, Math.round(score)));
  };
  return {
    summary:          raw.summary || result?.output || "AI Assist is watching.",
    labels:           Array.isArray(labels)
      ? labels.map((item) => (typeof item === "string" ? item : item.label || item.name || item.object)).filter(Boolean).slice(0, 5)
      : [],
    advice:           raw.advice || raw.shootingAdvice || "",
    filter:           raw.filterSuggestion || raw.filter || "",
    shootNow:         Boolean(raw.shootNow || raw.readyToCapture),
    confidence:       clamp100(raw.confidence),
    // â"€â"€ premium analysis fields â"€â"€
    compositionScore: clamp100(raw.compositionScore),
    lightingScore:    clamp100(raw.lightingScore),
    lightingType:     raw.lightingType || "",
    subject:          raw.subject || raw.primarySubject || "",
    mood:             raw.mood || raw.sceneMood || "",
    dominantColors:   Array.isArray(raw.dominantColors) ? raw.dominantColors.slice(0, 3) : [],
    recommendedMode:  raw.recommendedMode || raw.cameraMode || "",
    exposureNote:     raw.exposureNote || raw.exposure || "",
    provider:         result?.provider || raw.provider || "",
    fallback:         Boolean(result?.fallback || raw.fallback),
  };
}


// â"€â"€â"€ ai assist helpers â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const COLOR_MAP = {
  red:"#e85c5c", blue:"#5c9ee8", green:"#5ce87d", yellow:"#e8d45c",
  orange:"#e8945c", purple:"#a05ce8", pink:"#e85ca0", brown:"#a07850",
  white:"#dcdcdc", black:"#2a2a2a", gray:"#888888", grey:"#888888",
  cyan:"#5ce8e0", teal:"#42f6d7", gold:"#f4c95d", golden:"#f4c95d",
  beige:"#d4b896", lavender:"#b09cd8", maroon:"#8a3040", navy:"#2a3a6a",
  sky:"#87ceeb", silver:"#c0c0c0", cream:"#fffacd", olive:"#8a9a40",
};

function colorToCss(name) {
  if (!name) return "rgba(255,255,255,0.35)";
  const key = name.toLowerCase().trim();
  return COLOR_MAP[key] || COLOR_MAP[key.split(" ").find((w) => COLOR_MAP[w]) || ""] || "rgba(255,255,255,0.35)";
}

function matchFilterByName(name) {
  if (!name) return null;
  const lower = name.toLowerCase().trim();
  return (
    FILTERS.find(
      (f) =>
        f.name.toLowerCase() === lower ||
        f.name.toLowerCase().includes(lower) ||
        lower.includes(f.name.toLowerCase().split(" ")[0])
    )?.id || null
  );
}

// â"€â"€â"€ duration helper â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function formatDuration(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function getSupportedMimeType() {
  // H.264 is hardware-accelerated on virtually all mobile SoCs - prefer it.
  // VP9 is software on most phones which competes with the encoder for CPU.
  const candidates = [
    "video/mp4;codecs=h264,aac",   // iOS Safari + Android (hardware H.264)
    "video/mp4;codecs=avc1",       // alternate H.264 label
    "video/mp4",                   // generic mp4
    "video/webm;codecs=h264,opus", // Chrome with H.264 in WebM
    "video/webm;codecs=vp8,opus",  // VP8 is lighter than VP9
    "video/webm;codecs=vp9,opus",  // VP9 - software encode, last resort
    "video/webm",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

// â"€â"€â"€ grid â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const GRID_TYPES = [
  { id: "thirds", notice: "Grid - Rule of Thirds" },
  { id: "square", notice: "Grid - 4 x 4"          },
  { id: "center", notice: "Grid - Center Cross"    },
  { id: "golden", notice: "Grid - Golden Ratio"    },
];

function GridOverlay({ type }) {
  return (
    <svg
      className={`grid-svg grid-type-${type}`}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {/* Rule of Thirds */}
      {type === "thirds" && (
        <>
          <line className="gd-v" x1="33.33" y1="0"     x2="33.33" y2="100"   />
          <line className="gd-v" x1="66.67" y1="0"     x2="66.67" y2="100"   />
          <line className="gd-h" x1="0"     y1="33.33" x2="100"   y2="33.33" />
          <line className="gd-h" x1="0"     y1="66.67" x2="100"   y2="66.67" />
          {[[33.33,33.33],[66.67,33.33],[33.33,66.67],[66.67,66.67]].map(([cx,cy],i) => (
            <g key={i} className="gd-ix">
              <line x1={cx-2.2} y1={cy} x2={cx+2.2} y2={cy} />
              <line x1={cx} y1={cy-1.0} x2={cx} y2={cy+1.0} />
            </g>
          ))}
        </>
      )}

      {/* 4x4 */}
      {type === "square" && (
        <>
          {[25,50,75].flatMap((v,i) => [
            <line key={`v${i}`} className="gd-v" x1={v} y1="0"   x2={v}   y2="100" />,
            <line key={`h${i}`} className="gd-h" x1="0" y1={v}   x2="100" y2={v}   />,
          ])}
        </>
      )}

      {/* Center Cross */}
      {type === "center" && (
        <>
          <line className="gd-v" x1="50" y1="0"  x2="50"  y2="100" />
          <line className="gd-h" x1="0"  y1="50" x2="100" y2="50"  />
          <path className="gd-bracket" d="M4,14 L4,4 L14,4"     fill="none" />
          <path className="gd-bracket" d="M86,4 L96,4 L96,14"   fill="none" />
          <path className="gd-bracket" d="M4,86 L4,96 L14,96"   fill="none" />
          <path className="gd-bracket" d="M96,86 L96,96 L86,96" fill="none" />
        </>
      )}

      {/* Golden Ratio */}
      {type === "golden" && (
        <>
          <line className="gd-v" x1="38.2" y1="0"    x2="38.2" y2="100"  />
          <line className="gd-v" x1="61.8" y1="0"    x2="61.8" y2="100"  />
          <line className="gd-h" x1="0"    y1="38.2" x2="100"  y2="38.2" />
          <line className="gd-h" x1="0"    y1="61.8" x2="100"  y2="61.8" />
          <line className="gd-phi-inner" x1="38.2" y1="38.2" x2="61.8" y2="61.8" />
          <line className="gd-phi-inner" x1="61.8" y1="38.2" x2="38.2" y2="61.8" />
        </>
      )}
    </svg>
  );
}

// â"€â"€â"€ constants â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const FILTER_SHORTCUTS = [
  ["Clean", "clean-pro"], ["Portrait", "portrait-glow"], ["LinkedIn", "linkedin-studio"],
  ["Gold", "cinematic-gold"], ["Teal", "teal-orange"], ["B&W", "bw-classic"],
  ["Product", "product-white"], ["Food", "food-warm"], ["Night", "night-bright"], ["Document", "document-sharp"]
];

const CAMERA_MODES_STRIP = [
  { id: "Social",    label: "Social",    filter: null },
  { id: "Food",      label: "Food",      filter: "food-warm" },
  { id: "Auto",      label: "Photo",     filter: null },
  { id: "Portrait",  label: "Portrait",  filter: "portrait-glow" },
  { id: "Product",   label: "Product",   filter: "product-white" },
  { id: "Document",  label: "Document",  filter: "document-sharp" },
  { id: "Night",     label: "Night",     filter: "night-bright" },
  { id: "Travel",    label: "Travel",    filter: "travel-vivid" },
  { id: "Macro",     label: "Macro",     filter: null },
  { id: "Cinematic", label: "Cinematic", filter: "cinematic-gold" },
];

const VIDEO_MODES_STRIP = [
  { id: "Normal",    label: "Normal",    filter: null,              notice: "Standard video" },
  { id: "Cinematic", label: "Cinematic", filter: "cinematic-gold",  notice: "Cinematic · warm tones" },
  { id: "Portrait",  label: "Portrait",  filter: "portrait-glow",   notice: "Portrait video" },
  { id: "Vlog",      label: "Vlog",      filter: "clean-pro",       notice: "Vlog · clean look" },
  { id: "Night",     label: "Night",     filter: "night-bright",    notice: "Night video · boosted" },
  { id: "Food",      label: "Food",      filter: "food-warm",       notice: "Food video · warm" },
  { id: "Action",    label: "Action",    filter: null,              notice: "Action · max fps" },
  { id: "SlowMo",    label: "Slow Mo",   filter: null,              notice: "Slow Mo · 60 fps capture" },
  { id: "Travel",    label: "Travel",    filter: "travel-vivid",    notice: "Travel · vivid" },
  { id: "Document",  label: "Doc",       filter: "document-sharp",  notice: "Documentary style" },
];

const ZOOM_PRESETS = [1, 2, 5];

// â"€â"€â"€ pro controls config â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const PC_TABS = [
  { id: "Capture",  label: "Capture",  Icon: Camera     },
  { id: "Exposure", label: "Exposure", Icon: SunMedium  },
  { id: "Focus",    label: "Focus",    Icon: Crosshair  },
  { id: "Video",    label: "Video",    Icon: Video      },
  { id: "WB",       label: "White Bal",Icon: Aperture   },
  { id: "View",     label: "View",     Icon: Eye        },
  { id: "Modes",    label: "Modes",    Icon: Layers     },
  { id: "AI",       label: "AI",       Icon: Bot        },
  { id: "Export",   label: "Export",   Icon: Download   },
];

const WB_PRESETS = [
  { id: "Auto",        label: "Auto",    kelvin: null, color: "linear-gradient(135deg,#aed6f1,#f9e4b7)" },
  { id: "Daylight",    label: "Daylight",kelvin: 5500, color: "linear-gradient(135deg,#ffe680,#f9c74f)" },
  { id: "Cloudy",      label: "Cloudy",  kelvin: 6500, color: "linear-gradient(135deg,#d6eaf8,#aac4e0)" },
  { id: "Shade",       label: "Shade",   kelvin: 7500, color: "linear-gradient(135deg,#8eafca,#6b93b8)" },
  { id: "Tungsten",    label: "Tungsten",kelvin: 3200, color: "linear-gradient(135deg,#f4a460,#c0622a)" },
  { id: "Fluorescent", label: "Fluor.",  kelvin: 4200, color: "linear-gradient(135deg,#d4efdf,#a9dfbf)" },
  { id: "Flash",       label: "Flash",   kelvin: 5500, color: "linear-gradient(135deg,#eaf4fb,#c8e6f5)" },
];

// â"€â"€â"€ vertical zoom slider â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

function VZoomSlider({ value, min, max, onChange }) {
  const trackRef  = useRef(null);
  const dragging  = useRef(false);
  const [active, setActive] = useState(false);

  const valueFromY = (clientY) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return value;
    // top of track = max zoom, bottom = min zoom â†' swipe up zooms in
    const frac = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    return +(min + frac * (max - min)).toFixed(2);
  };

  const onDown = (e) => {
    dragging.current = true;
    setActive(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    onChange(valueFromY(e.clientY));
  };
  const onMove = (e) => { if (dragging.current) onChange(valueFromY(e.clientY)); };
  const onUp   = ()  => { dragging.current = false; setActive(false); };

  const pct   = max > min ? Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100)) : 0;
  const ticks = ZOOM_PRESETS.filter((z) => z >= min && z <= max);

  return (
    <div
      ref={trackRef}
      className={`vzoom-track${active ? " is-active" : ""}`}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {/* Background rail */}
      <div className="vzoom-rail" />

      {/* Filled portion - bottom â†' current zoom */}
      <div className="vzoom-fill" style={{ height: `${pct}%` }} />

      {/* Preset tick marks */}
      {ticks.map((z) => {
        const topPct = 100 - ((z - min) / (max - min)) * 100;
        const near   = Math.abs(value - z) < 0.18;
        return (
          <div key={z} className={`vzoom-tick${near ? " is-near" : ""}`} style={{ top: `${topPct}%` }}>
            <span className="vzoom-tick-line" />
            <span className="vzoom-tick-label">{z}x</span>
          </div>
        );
      })}

      {/* Draggable knob */}
      <div
        className={`vzoom-thumb${active ? " is-active" : ""}`}
        style={{ top: `clamp(0px, calc(${100 - pct}% - 14px), calc(100% - 28px))` }}
      >
        <span className="vzoom-thumb-glow" />
        <span className="vzoom-thumb-core" />
      </div>
    </div>
  );
}

// â"€â"€â"€ component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€


// -- save to device gallery -------------------------------------------------------
function saveToDeviceGallery(blob,filename){try{var url=URL.createObjectURL(blob);var a=document.createElement("a");a.href=url;a.download=filename;a.style.display="none";document.body.appendChild(a);a.click();document.body.removeChild(a);setTimeout(function(){URL.revokeObjectURL(url);},15000);}catch(e){}}
function galleryFilename(type,mimeType){var ts=new Date().toISOString().slice(0,19).replace(/[T:]/g,"-");var ext=type==="video"?((mimeType||"").includes("mp4")?"mp4":"webm"):"jpg";return "LensPro_"+ts+"."+ext;}

export default function CameraView({ settings, latestPhoto, onPhotoCaptured, onNavigate, online }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const assistBusyRef = useRef(false);
  const lastPinchDistRef = useRef(null);
  const lastPinchZoomRef = useRef(1);
  // stale-closure-safe refs for DOM touch handlers
  const supportRef = useRef({});
  const zoomStateRef = useRef(1);
  const digitalZoomRef = useRef(1);
  const zoomHintTimerRef = useRef(null);
  // video recording
  const mediaRecorderRef   = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef  = useRef(null);
  const recordingStartRef  = useRef(null);
  const eisCleanupRef         = useRef(null); // EIS canvas cleanup fn
  const aiAutoCapturedRef     = useRef(false); // prevents double-fire on auto-capture
  const shakeSmoothRef        = useRef(0);     // EMA accumulator for DeviceMotion
  const histCanvasRef         = useRef(null);  // live histogram canvas
  const histRafRef            = useRef(null);  // live histogram RAF handle
  const audioCtxRef           = useRef(null);  // recording audio context
  const audioAnalyserRef      = useRef(null);  // recording analyser node
  const audioVizRafRef        = useRef(null);  // recording viz RAF handle
  const audioVizCanvasRef     = useRef(null);  // recording viz canvas
  const audioSmoothedRef      = useRef(null);  // per-bar smoothed values
  // feature: burst
  const burstIntervalRef      = useRef(null);
  const burstTimeoutRef       = useRef(null);
  // feature: swipe mode switch
  const swipeTouchRef         = useRef({ x: 0, y: 0, t: 0, active: false });
  // new features
  const intervalTimerRef      = useRef(null);
  const paletteRafRef         = useRef(null);
  const runCaptureRef         = useRef(null);   // stale-closure-safe ref for interval shot

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [facingMode, setFacingMode] = useState(settings.defaultCamera || "environment");
  const [torch, setTorchState] = useState(false);
  const [support, setSupport] = useState({});
  const [filterId, setFilterId] = useState(settings.defaultFilter || "clean-pro");
  const [filterStrength, setFilterStrength] = useState(85);
  const [filterFavorites, setFilterFavorites] = useState([]);
  const [filterAdjust, setFilterAdjust] = useState({});
  const [timer, setTimer] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [grid, setGrid] = useState(Boolean(settings.grid));
  const [gridType, setGridType] = useState("thirds");
  const [mirror, setMirror] = useState(Boolean(settings.mirrorSelfie));
  const [zoom, setZoomState] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [focusPoint, setFocusPoint] = useState(null);
  const [aiAssistOn, setAiAssistOn] = useState(false);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistError, setAiAssistError] = useState("");
  const [aiAssistResult, setAiAssistResult] = useState(null);
  const [aiAutoCapture, setAiAutoCapture] = useState(false);
  const [showProControls, setShowProControls] = useState(false);
  const [cameraMode, setCameraMode] = useState("Auto");
  const [shootingFormat, setShootingFormat] = useState(settings.exportFormat?.toUpperCase?.() || "JPG");
  const [ratio, setRatio] = useState("Full");
  const [watermark, setWatermark] = useState(false);
  const [hdr, setHdr] = useState(false);
  const [burst, setBurst] = useState(false);
  const [rawIntent, setRawIntent] = useState(false);
  const [livePhoto, setLivePhoto] = useState(false);
  const [focusLock, setFocusLock] = useState(false);
  const [exposureLock, setExposureLock] = useState(false);
  const [whiteBalanceLock, setWhiteBalanceLock] = useState(false);
  const [histogram, setHistogram]   = useState(false);
  const [zebra, setZebra]           = useState(false);
  const [focusPeaking, setFocusPeaking] = useState(false);
  const [antiShake, setAntiShake]   = useState(false);
  const [shakeLevel, setShakeLevel] = useState(0);       // 0 = still, 100 = very shaky
  // feature: burst
  const [isBursting, setIsBursting]   = useState(false);
  const [burstCount, setBurstCount]   = useState(0);
  // feature: swipe mode hint
  const [modeHint, setModeHint]       = useState(null);  // { label, dir }
  const [minimalUi, setMinimalUi] = useState(false);
  const [oneHand, setOneHand] = useState(false);
  const [proNotice, setProNotice] = useState("");
  // enterprise additions
  const [captureFlash, setCaptureFlash] = useState(false);
  const [captureFiring, setCaptureFiring] = useState(false);
  const [ev, setEv] = useState(0);
  const [showEv, setShowEv] = useState(false);
  const [digitalZoom, setDigitalZoom] = useState(1);
  const [showZoomHint, setShowZoomHint] = useState(false);
  // video recording
  const [shootMode, setShootMode] = useState("photo"); // "photo" | "video"
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [videoMode, setVideoMode] = useState("Normal");
  const [motionDetect, setMotionDetect] = useState(false);
  // â"€â"€ pro controls new state â"€â"€
  const [proTab, setProTab] = useState("Capture");
  const [iso, setIso] = useState("Auto");
  const [shutterSpeed, setShutterSpeed] = useState("Auto");
  const [whiteBalance, setWhiteBalance] = useState("Auto");
  const [focusMode, setFocusMode] = useState("Auto");
  const [videoResolution, setVideoResolution] = useState("1080p");
  const [videoFps, setVideoFps] = useState(30);
  const [stabilization, setStabilization] = useState(false);
  const [noiseReduction, setNoiseReduction] = useState(false);
  const [timelapse, setTimelapse] = useState(false);
  // ── 5 new features replacing Level ──────────────────────────────────
  const [intervalSecs, setIntervalSecs]   = useState(0);     // 0=off, 2, 5, 10, 30
  const [colorPaletteOn, setColorPaletteOn] = useState(false);
  const [paletteColors, setPaletteColors] = useState([]);
  const [evLocked, setEvLocked]           = useState(false);
  const [steadyState, setSteadyState]     = useState("idle"); // "idle"|"steady"|"moving"
  // ── Enterprise state ─────────────────────────────────────────────────
  const [activeProject,   setActiveProjectState]   = useState(null);
  const [activeChecklist, setActiveChecklistState] = useState(null);
  const [syncPending,     setSyncPending]           = useState(0);

  const activeFilter = getFilterById(filterId);
  const cameraInteractive = status === "ready" || status === "capturing";

  // EV-aware live filter: applies EV + fine-tune adjustments on top of selected filter
  const liveFilter = useMemo(() => {
    const base = mergeFilterSettings(activeFilter, filterAdjust, filterStrength);
    const evAdjusted = ev !== 0 ? { ...base, brightness: (base.brightness || 0) + ev * 30 } : base;
    return cssFilterFromSettings(evAdjusted);
  }, [activeFilter, filterAdjust, filterStrength, ev]);

  // Video transform: mirror + digital zoom (CSS scale fallback)
  const videoTransform = useMemo(() => {
    const parts = [];
    if (facingMode === "user" && mirror) parts.push("scaleX(-1)");
    if (digitalZoom !== 1) parts.push(`scale(${digitalZoom.toFixed(3)})`);
    return parts.length ? parts.join(" ") : "none";
  }, [facingMode, mirror, digitalZoom]);

  const showNotice = useCallback((message) => {
    setProNotice(message);
    setTimeout(() => setProNotice(""), 2800);
  }, []);

  // shared helper - shows a standard "web/PWA limitation" notice
  const limited = useCallback((feature) => {
    showNotice(`${feature}: prepared as a web workflow - native hardware support depends on your device & browser.`);
  }, [showNotice]);


  // -- anti-shake (DeviceMotion RMS smoothed with EMA) -----------------------
  useEffect(() => {
    if (!antiShake || !cameraInteractive) { setShakeLevel(0); return undefined; }
    shakeSmoothRef.current = 0;
    const ALPHA = 0.22;
    const handleMotion = (e) => {
      const a = e.acceleration || e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      const rms = Math.sqrt(a.x ** 2 + a.y ** 2 + (a.z ?? 0) ** 2);
      shakeSmoothRef.current = shakeSmoothRef.current * (1 - ALPHA) + rms * ALPHA;
      setShakeLevel(Math.min(100, Math.round(shakeSmoothRef.current * 22)));
    };
    window.addEventListener("devicemotion", handleMotion);
    return () => { window.removeEventListener("devicemotion", handleMotion); setShakeLevel(0); };
  }, [antiShake, cameraInteractive]);

  // ── Stabilisation badge — always-on when camera ready ──────────────────────
  // Lightweight motion listener separate from antiShake. Shows STEADY/MOVING
  // badge near the shutter so users know when to press capture.
  // Disabled when filters panel is open or motion detection is active.
  useEffect(() => {
    if (!cameraInteractive || showFilters || motionDetect) { setSteadyState("idle"); return undefined; }
    let smoothed = 0;
    const ALPHA = 0.20;
    const handle = (e) => {
      const a = e.acceleration || e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      const rms = Math.sqrt(a.x ** 2 + a.y ** 2 + (a.z ?? 0) ** 2);
      smoothed = smoothed * (1 - ALPHA) + rms * ALPHA;
      setSteadyState(smoothed < 0.9 ? "steady" : "moving");
    };
    window.addEventListener("devicemotion", handle, { passive: true });
    return () => { window.removeEventListener("devicemotion", handle); setSteadyState("idle"); };
  }, [cameraInteractive, showFilters, motionDetect]);

  // ── Enterprise: load active project, checklist, sync count ─────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [proj, chk] = await Promise.all([getActiveProject(), getActiveChecklist()]);
      if (!cancelled) {
        setActiveProjectState(proj);
        setActiveChecklistState(chk);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Interval shot — auto-capture at set interval ────────────────────────────
  // runCaptureRef keeps the latest runCapture without re-creating the timer.
  useEffect(() => { runCaptureRef.current = runCapture; });
  useEffect(() => {
    clearInterval(intervalTimerRef.current);
    if (!intervalSecs || !cameraInteractive || shootMode !== "photo") return;
    intervalTimerRef.current = setInterval(() => {
      runCaptureRef.current?.();
    }, intervalSecs * 1000);
    return () => clearInterval(intervalTimerRef.current);
  }, [intervalSecs, cameraInteractive, shootMode]);

  // ── Live colour palette — sample video every 900 ms ─────────────────────────
  useEffect(() => {
    if (!colorPaletteOn || !cameraInteractive) {
      cancelAnimationFrame(paletteRafRef.current);
      setPaletteColors([]);
      return undefined;
    }
    let lastSample = 0;
    const cvs = document.createElement("canvas");
    cvs.width = 90; cvs.height = 50;
    const ctx = cvs.getContext("2d", { willReadFrequently: true });
    const draw = (ts) => {
      paletteRafRef.current = requestAnimationFrame(draw);
      if (ts - lastSample < 900) return;
      lastSample = ts;
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      ctx.drawImage(video, 0, 0, 90, 50);
      const px = ctx.getImageData(0, 0, 90, 50).data;
      // Average colour across 3 horizontal zones
      const colors = [0, 1, 2].map((zone) => {
        let r = 0, g = 0, b = 0, n = 0;
        const x0 = zone * 30, x1 = x0 + 30;
        for (let x = x0; x < x1; x++) {
          for (let y = 0; y < 50; y++) {
            const i = (y * 90 + x) * 4;
            r += px[i]; g += px[i + 1]; b += px[i + 2]; n++;
          }
        }
        return `rgb(${Math.round(r/n)},${Math.round(g/n)},${Math.round(b/n)})`;
      });
      setPaletteColors(colors);
    };
    paletteRafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(paletteRafRef.current);
  }, [colorPaletteOn, cameraInteractive]);

  // -- live color histogram (samples video every 280 ms) ----------------------
  useEffect(() => {
    if (!histogram || !cameraInteractive) {
      cancelAnimationFrame(histRafRef.current); return undefined;
    }
    let lastSample = 0;
    const sample = document.createElement("canvas");
    sample.width = 160; sample.height = 90;
    const sctx = sample.getContext("2d", { willReadFrequently: true });
    const draw = (ts) => {
      histRafRef.current = requestAnimationFrame(draw);
      if (ts - lastSample < 280) return;
      lastSample = ts;
      const video = videoRef.current;
      const canvas = histCanvasRef.current;
      if (!video || !canvas || !video.videoWidth) return;
      sctx.drawImage(video, 0, 0, 160, 90);
      const px = sctx.getImageData(0, 0, 160, 90).data;
      const R = new Uint32Array(256), G = new Uint32Array(256),
            B = new Uint32Array(256), L = new Uint32Array(256);
      for (let i = 0; i < px.length; i += 4) {
        R[px[i]]++; G[px[i + 1]]++; B[px[i + 2]]++;
        L[Math.round(px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114)]++;
      }
      const ctx = canvas.getContext("2d");
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      let mx = 1;
      for (let i = 0; i < 256; i++) mx = Math.max(mx, L[i], R[i], G[i], B[i]);
      const channels = [
        { d: L, fill: "rgba(255,255,255,0.38)" },
        { d: R, fill: "rgba(255,72,72,0.62)" },
        { d: G, fill: "rgba(56,210,76,0.62)" },
        { d: B, fill: "rgba(68,122,255,0.62)" },
      ];
      const bw = W / 256;
      for (const ch of channels) {
        ctx.beginPath();
        ctx.moveTo(0, H);
        for (let i = 0; i < 256; i++) ctx.lineTo(i * bw, H - (ch.d[i] / mx) * H * 0.96);
        ctx.lineTo(W, H);
        ctx.closePath();
        ctx.fillStyle = ch.fill;
        ctx.fill();
      }
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 0.5;
      [0.25, 0.5, 0.75].forEach((f) => {
        ctx.beginPath(); ctx.moveTo(W * f, 0); ctx.lineTo(W * f, H); ctx.stroke();
      });
    };
    histRafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(histRafRef.current);
  }, [histogram, cameraInteractive]);

  // -- recording audio visualiser RAF loop — hyper premium ───────────────────
  useEffect(() => {
    if (!isRecording) {
      cancelAnimationFrame(audioVizRafRef.current);
      audioSmoothedRef.current = null;
      return undefined;
    }
    const BAR_COUNT = 54;
    // initialise per-bar smoothing array
    if (!audioSmoothedRef.current) {
      audioSmoothedRef.current = new Float32Array(BAR_COUNT);
    }
    const draw = () => {
      audioVizRafRef.current = requestAnimationFrame(draw);
      const canvas   = audioVizCanvasRef.current;
      const analyser = audioAnalyserRef.current;
      if (!canvas || !analyser) return;
      const ctx  = canvas.getContext("2d");
      const W    = canvas.width;
      const H    = canvas.height;
      const bins = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(bins);
      ctx.clearRect(0, 0, W, H);

      const BAR_W  = 4.2;
      const GAP    = 1.8;
      const TOTAL  = BAR_COUNT * (BAR_W + GAP) - GAP;
      const startX = (W - TOTAL) / 2;
      const CY     = H / 2;
      const MAX_H  = CY - 2;
      const smoothed = audioSmoothedRef.current;

      for (let i = 0; i < BAR_COUNT; i++) {
        const bin = Math.floor(i * analyser.frequencyBinCount / BAR_COUNT);
        const raw = bins[bin] / 255;

        // exponential smoothing: fast attack, slow decay
        smoothed[i] = raw > smoothed[i]
          ? raw * 0.75 + smoothed[i] * 0.25
          : raw * 0.08 + smoothed[i] * 0.92;

        const val = smoothed[i];
        const bh  = Math.max(2.5, val * MAX_H);
        const x   = startX + i * (BAR_W + GAP);

        const g = ctx.createLinearGradient(0, CY - bh, 0, CY + bh);

        if (val > 0.82) {
          // ── peak clipping zone → amber-red warning ──
          g.addColorStop(0,   "rgba(255,220,80,0.98)");
          g.addColorStop(0.25,"rgba(255,140,40,0.95)");
          g.addColorStop(0.5, "rgba(255,55,55,0.90)");
          g.addColorStop(0.75,"rgba(255,140,40,0.95)");
          g.addColorStop(1,   "rgba(255,220,80,0.98)");
          ctx.shadowColor = "rgba(255,70,40,0.9)";
          ctx.shadowBlur  = 18;
        } else if (val > 0.04) {
          // ── active zone → brilliant white-teal ──
          const t = Math.min(1, val * 1.3);
          g.addColorStop(0,    `rgba(255,255,255,${(0.92 + t * 0.08).toFixed(2)})`);
          g.addColorStop(0.18, `rgba(160,255,245,${(0.88 + t * 0.12).toFixed(2)})`);
          g.addColorStop(0.5,  `rgba(42,230,200,${(0.78 + t * 0.22).toFixed(2)})`);
          g.addColorStop(0.82, `rgba(160,255,245,${(0.88 + t * 0.12).toFixed(2)})`);
          g.addColorStop(1,    `rgba(255,255,255,${(0.92 + t * 0.08).toFixed(2)})`);
          ctx.shadowColor = `rgba(66,246,215,${(0.45 + val * 0.55).toFixed(2)})`;
          ctx.shadowBlur  = 6 + val * 22;
        } else {
          // ── idle → ghost teal ──
          g.addColorStop(0,   "rgba(66,246,215,0.22)");
          g.addColorStop(0.5, "rgba(66,246,215,0.10)");
          g.addColorStop(1,   "rgba(66,246,215,0.22)");
          ctx.shadowBlur = 0;
        }

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.roundRect(x, CY - bh, BAR_W, bh * 2, BAR_W / 2);
        ctx.fill();
      }
      ctx.shadowBlur = 0;
    };
    audioVizRafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(audioVizRafRef.current);
  }, [isRecording]);

  // -- camera boot --
  const bootCamera = useCallback(async () => {
    // Abort any active recording before tearing down the stream
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
    eisCleanupRef.current?.();
    eisCleanupRef.current = null;
    setIsRecording(false);

    setStatus("loading");
    setError("");
    try {
      stopStream(streamRef.current);
      const stream = await startCamera({ facingMode });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) { video.srcObject = stream; await video.play(); }
      const track = stream.getVideoTracks()[0];
      const capabilities = readTrackSupport(track);
      setSupport(capabilities);
      setZoomState(capabilities.settings?.zoom || capabilities.zoom?.min || 1);
      setStatus("ready");
    } catch (cameraError) {
      setError(
        cameraError?.name === "NotAllowedError"
          ? "Camera permission was denied. Allow camera access in browser settings to use LensPro capture."
          : cameraError.message || "Camera is unavailable."
      );
      setStatus("error");
    }
  }, [facingMode]);

  useEffect(() => {
    bootCamera();
    return () => stopStream(streamRef.current);
  }, [bootCamera]);

  // â"€â"€ App visibility / foreground restore â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
  // The OS kills the camera stream whenever the PWA is backgrounded (user
  // switches to another app or locks the screen).  We restart it on return.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Stream is dead after backgrounding - boot a fresh one.
        bootCamera();
      } else {
        // Stop any active recording so the onstop handler can finalise the blob
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
        clearInterval(recordingTimerRef.current);
        eisCleanupRef.current?.();
        eisCleanupRef.current = null;
        setIsRecording(false);
        // Release camera hardware while backgrounded
        stopStream(streamRef.current);
        streamRef.current = null;
      }
    };

    const onPageShow = (e) => {
      // iOS restores pages from the bfcache when returning from another app.
      // The video stream is always dead after a bfcache restore.
      if (e.persisted) bootCamera();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [bootCamera]);

  // â"€â"€ keep refs current so DOM handlers never have stale closures â"€â"€
  useEffect(() => { supportRef.current   = support; }, [support]);
  useEffect(() => { zoomStateRef.current = zoom;    }, [zoom]);

  // â"€â"€ pinch-to-zoom - DOM listeners with passive:false so preventDefault works â"€â"€
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    function flashZoomHint() {
      setShowZoomHint(true);
      clearTimeout(zoomHintTimerRef.current);
      zoomHintTimerRef.current = setTimeout(() => setShowZoomHint(false), 1400);
    }

    function onTouchStart(e) {
      if (e.touches.length !== 2) return;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDistRef.current = Math.sqrt(dx * dx + dy * dy);
      // snapshot baseline zoom - hardware or digital
      lastPinchZoomRef.current = supportRef.current.zoom
        ? zoomStateRef.current
        : digitalZoomRef.current;
    }

    function onTouchMove(e) {
      if (e.touches.length !== 2 || !lastPinchDistRef.current) return;
      e.preventDefault(); // blocks browser page-zoom; only works with passive:false
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / lastPinchDistRef.current;
      flashZoomHint();

      const zs = supportRef.current.zoom;
      if (zs) {
        // Hardware zoom up to device max, then digital zoom layers on top for 10x
        const hwMax   = zs.max || 10;
        const totalMax = Math.max(hwMax, 10);
        const next    = Math.max(zs.min || 1, Math.min(totalMax, lastPinchZoomRef.current * scale));
        setZoomState(next);
        zoomStateRef.current = next;
        const extra = next > hwMax ? next / hwMax : 1;
        digitalZoomRef.current = extra;
        setDigitalZoom(extra);
        setZoom(streamRef.current, Math.min(next, hwMax)).catch(() => {});
      } else {
        // Digital zoom - CSS scale on the video element
        const next = Math.max(1, Math.min(10, lastPinchZoomRef.current * scale));
        digitalZoomRef.current = next;
        setDigitalZoom(next);
      }
    }

    function onTouchEnd() {
      lastPinchDistRef.current = null;
    }

    video.addEventListener("touchstart", onTouchStart, { passive: true });
    video.addEventListener("touchmove",  onTouchMove,  { passive: false });
    video.addEventListener("touchend",   onTouchEnd,   { passive: true });

    return () => {
      video.removeEventListener("touchstart", onTouchStart);
      video.removeEventListener("touchmove",  onTouchMove);
      video.removeEventListener("touchend",   onTouchEnd);
      clearTimeout(zoomHintTimerRef.current);
    };
  }, []); // empty deps - all mutable values read via refs

  // â"€â"€ AI assist â"€â"€
  const analyzeFrame = useCallback(async () => {
    if (!online) {
      setAiAssistError("AI Assist requires internet. Camera, filters, editor, and gallery still work offline.");
      setAiAssistOn(false);
      return;
    }
    if (!videoRef.current || status !== "ready" || assistBusyRef.current) return;
    assistBusyRef.current = true;
    setAiAssistLoading(true);
    setAiAssistError("");
    try {
      const imageDataUrl = captureAssistDataUrl(videoRef.current);
      const result = await requestGroqAnalysis({
        action: "Live camera assist",
        imageDataUrl,
        provider: "auto",
        prompt: [
          "Analyze this camera preview frame for a professional AI camera assistant.",
          "Return ONLY valid JSON with these exact keys:",
          "summary (1 sentence description),",
          "labels (array of 3-5 short visible object/scene labels),",
          "advice (one short actionable photo tip for right now - max 12 words),",
          "filterSuggestion (choose from: Clean Pro, Portrait Glow, Cinematic Gold, Teal Orange Film, Classic B&W, Food Warm, Travel Vivid, Night Bright, Document Sharp - or null),",
          "shootNow (boolean - true only when composition, focus, and lighting are all good),",
          "confidence (0-100 shot readiness score),",
          "compositionScore (0-100 composition quality - rule of thirds, balance, leading lines),",
          "lightingScore (0-100 lighting quality),",
          "lightingType (single word from: natural, golden, harsh, soft, artificial, low-light, backlit, mixed),",
          "subject (2-4 words describing the primary subject),",
          "mood (single word from: dramatic, serene, energetic, intimate, vibrant, mysterious, warm, cool, neutral),",
          "dominantColors (array of 2-3 basic color names visible in the scene),",
          "recommendedMode (one of: Auto, Portrait, Food, Travel, Night, Document, Product),",
          "exposureNote (3-6 words e.g. well exposed, slightly underexposed, overexposed highlights).",
          "Return ONLY the JSON object. No extra text."
        ].join(" ")
      });
      setAiAssistResult(normalizeAssistResult(result));
    } catch (assistError) {
      setAiAssistError(assistError.message || "AI Assist failed. Try again.");
    } finally {
      assistBusyRef.current = false;
      setAiAssistLoading(false);
    }
  }, [online, status]);

  useEffect(() => {
    if (!aiAssistOn || !cameraInteractive) return undefined;
    analyzeFrame();
    const interval = window.setInterval(analyzeFrame, 10000);
    return () => window.clearInterval(interval);
  }, [aiAssistOn, analyzeFrame, cameraInteractive]);
  // â"€â"€ capture â"€â"€
  const runCapture = useCallback(async () => {
    if (countdown) return;
    if (timer) {
      for (let i = timer; i > 0; i -= 1) {
        setCountdown(i);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      setCountdown(0);
    }
    try {
      setStatus("capturing");
      setCaptureFiring(true);
      setTimeout(() => setCaptureFiring(false), 500);
      if (settings.hapticFeedback !== false) vibrate();
      setCaptureFlash(true);
      setTimeout(() => setCaptureFlash(false), 520);
      const raw = await captureFrame(videoRef.current, { mirror: facingMode === "user" && mirror });

      // ── Auto-enhance (levels, WB, gamma, vibrance, sharpen) ────────────────
      const base = settings.autoEnhance !== false
        ? await autoEnhanceBlob(raw).catch(() => raw)   // fallback to raw on any error
        : raw;

      let edited = filterId === "none" ? base : await applyFilterToBlob(base, activeFilter, { strength: filterStrength });

      // ── Load watermark settings if needed ───────────────────────────────────
      const wmSettings = watermark ? await getWatermarkSettings() : null;

      // ── Apply watermark if enabled ──────────────────────────────────────────
      if (watermark && wmSettings?.enabled !== false) {
        try {
          const wmText = buildWatermarkText(wmSettings?.template || "{app} · {datetime}", wmSettings);
          edited = await applyWatermark(edited, { ...wmSettings, text: wmText });
        } catch { /* watermark failure is non-fatal */ }
      }

      // ── Build enterprise meta ───────────────────────────────────────────────
      const enterpriseMeta = {};
      if (activeProject) {
        enterpriseMeta.projectId    = activeProject.id;
        enterpriseMeta.projectName  = activeProject.name;
        enterpriseMeta.projectColor = activeProject.color;
      }

      const photo = await savePhoto({
        blob: settings.saveOriginal === false ? edited : base,
        editedBlob: settings.saveEdited === false ? undefined : edited,
        meta: {
          filterId,
          type: "photo",
          name: `LensPro capture ${new Date().toLocaleTimeString()}`,
          reviewStatus: "none",
          ...enterpriseMeta,
        }
      });
      onPhotoCaptured(photo);
      if (settings.saveToDevice !== false) saveToDeviceGallery(edited, galleryFilename("photo"));

      // ── Auto-categorize in background (non-blocking) ─────────────────────
      if (settings.autoCategorize && navigator.onLine) {
        blobToDataUrl(edited).then((dataUrl) =>
          categorizeImage(dataUrl).then((cat) => {
            if (cat.category !== "other") {
              putRecord("photos", {
                ...photo,
                aiCategory:           cat.category,
                aiCategoryConfidence: cat.confidence,
                aiCategoryReason:     cat.reason,
              });
            }
          })
        ).catch(() => {});
      }

      // ── Tick checklist if active ─────────────────────────────────────────
      if (activeChecklist) {
        const nextItem = activeChecklist.items.find((it) => !it.captured);
        if (nextItem) {
          tickChecklistItem(activeChecklist.id, nextItem.id, photo.id)
            .then((updated) => setActiveChecklistState(updated))
            .catch(() => {});
        }
      }

      // ── Add to sync queue if offline ─────────────────────────────────────
      if (!navigator.onLine) {
        addToSyncQueue({ photoId: photo.id, photoName: photo.name }).catch(() => {});
        setSyncPending((n) => n + 1);
      }

      setStatus("ready");
    } catch (captureError) {
      setError(captureError.message || "Capture failed. Try again.");
      setStatus("ready");
    }
  }, [activeChecklist, activeFilter, activeProject, countdown, facingMode, filterId, filterStrength, mirror, onPhotoCaptured, settings.autoCategorize, settings.saveEdited, settings.saveOriginal, timer, watermark]);


  // -- burst capture (lightweight) -------------------------------------------
  const runBurstFrame = useCallback(async () => {
    if (status !== "ready") return;
    try {
      const raw = await captureFrame(videoRef.current, { mirror: facingMode === "user" && mirror });
      const edited = filterId === "none" ? raw : await applyFilterToBlob(raw, activeFilter, { strength: filterStrength });
      const photo = await savePhoto({
        blob: settings.saveOriginal === false ? edited : raw,
        editedBlob: settings.saveEdited === false ? undefined : edited,
        meta: { filterId, type: "photo", name: "LensPro burst " + new Date().toLocaleTimeString() },
      });
      onPhotoCaptured(photo);
      if (settings.saveToDevice !== false) saveToDeviceGallery(edited, galleryFilename("photo"));
      setBurstCount((n) => n + 1);
    } catch { }
  }, [status, facingMode, mirror, filterId, activeFilter, filterStrength, settings, onPhotoCaptured]);

  const handleShutterDown = useCallback((e) => {
    if (status !== "ready" || shootMode !== "photo") return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch(_) {}
    burstTimeoutRef.current = setTimeout(() => {
      if (settings.hapticFeedback !== false) vibrate();
      setIsBursting(true); setBurstCount(0);
      runBurstFrame();
      burstIntervalRef.current = setInterval(runBurstFrame, 200);
    }, 400);
  }, [status, shootMode, settings.hapticFeedback, runBurstFrame]);

  const handleShutterUp = useCallback(() => {
    clearTimeout(burstTimeoutRef.current);
    if (burstIntervalRef.current) {
      clearInterval(burstIntervalRef.current); burstIntervalRef.current = null;
      const total = burstCount;
      setIsBursting(false); setBurstCount(0);
      showNotice("Burst · " + total + " shots");
    } else { runCapture(); }
  }, [burstCount, runCapture, showNotice]);

  const handleShutterCancel = useCallback(() => {
    clearTimeout(burstTimeoutRef.current);
    clearInterval(burstIntervalRef.current); burstIntervalRef.current = null;
    setIsBursting(false); setBurstCount(0);
  }, []);

  // â"€â"€ AI auto-capture - fires once when AI says "shoot now" and auto mode is on â"€â"€
  useEffect(() => {
    if (!aiAutoCapture || !aiAssistResult) {
      aiAutoCapturedRef.current = false;
      return;
    }
    if (!aiAssistResult.shootNow) {
      aiAutoCapturedRef.current = false; // reset for the next opportunity
      return;
    }
    if (aiAutoCapturedRef.current) return; // already fired for this result
    if (status !== "ready" || shootMode !== "photo" || countdown > 0) return;
    aiAutoCapturedRef.current = true;
    showNotice("AI Auto-Capture - perfect moment!");
    runCapture();
  }, [aiAutoCapture, aiAssistResult, countdown, runCapture, shootMode, status, showNotice]);

  // â"€â"€ video recording â"€â"€
  const startRecording = useCallback(async () => {
    if (!streamRef.current || isRecording) return;

    // â"€â"€ Ask the camera track to enable hardware stabilization if available â"€â"€
    const track = streamRef.current.getVideoTracks()[0];
    if (track?.applyConstraints) {
      // `imageStabilization` is a Chrome-on-Android extension; safe to try & ignore errors
      try { await track.applyConstraints({ advanced: [{ imageStabilization: true }] }); } catch { /* not supported */ }
    }

    // â"€â"€ Build the record stream (video from camera + separate audio) â"€â"€â"€â"€â"€
    // We create a NEW MediaStream with just the video track(s) so that
    // stopping the audio later never kills the camera preview.
    // ── Build the record stream (video from camera + separate audio) ──────
    // We create a NEW MediaStream with just the video track(s) so that
    // stopping the audio later never kills the camera preview.
    const recordStream = new MediaStream(streamRef.current.getVideoTracks());

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      const audioTrack  = audioStream.getAudioTracks()[0];
      if (audioTrack) {
        recordStream.addTrack(audioTrack);
        recordingChunksRef._audioTrack = audioTrack;
      }
    } catch {
      recordingChunksRef._audioTrack = null;
    }

    // â"€â"€ MediaRecorder with H.264 preference + tight bitrate â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
    const mimeType   = getSupportedMimeType();
    const recOptions = {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: 10_000_000,
      audioBitsPerSecond: 128_000,
    };

    let recorder;
    try {
      recorder = new MediaRecorder(recordStream, recOptions);
    } catch {
      try { recorder = new MediaRecorder(recordStream, mimeType ? { mimeType } : undefined); }
      catch {
        showNotice("Video recording is not supported on this device.");
        return;
      }
    }

    recordingChunksRef.current  = [];
    mediaRecorderRef.current    = recorder;
    recordingStartRef.current   = Date.now();

    // ── audio visualiser ──────────────────────────────────────────────────────
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.78;
      const src = audioCtx.createMediaStreamSource(recordStream);
      src.connect(analyser);
      audioCtxRef.current      = audioCtx;
      audioAnalyserRef.current = analyser;
    } catch { /* not critical — viz simply won't show */ }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      clearInterval(recordingTimerRef.current);
      recordingChunksRef._audioTrack?.stop();
      recordingChunksRef._audioTrack = null;
      const duration = recordingStartRef.current
        ? Math.round((Date.now() - recordingStartRef.current) / 1000) : 0;
      const blob = new Blob(recordingChunksRef.current, { type: mimeType || "video/webm" });
      recordingChunksRef.current = [];
      try {
        const saved = await savePhoto({
          blob,
          meta: { type: "video", name: `LensPro video ${new Date().toLocaleTimeString()}`, duration },
        });
        onPhotoCaptured(saved);
        if (settings.saveToDevice !== false) saveToDeviceGallery(blob, galleryFilename("video", mimeType));
        showNotice(`Video saved · ${formatDuration(duration)}`);
      } catch {
        showNotice("Video save failed.");
      }
    };

    recorder.start(1000);
    setIsRecording(true);
    setRecordingDuration(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingDuration(Math.round((Date.now() - recordingStartRef.current) / 1000));
    }, 500);
    if (settings.hapticFeedback !== false) vibrate();
    showNotice("Recording started");
  }, [isRecording, onPhotoCaptured, showNotice]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
    eisCleanupRef.current?.();
    eisCleanupRef.current = null;
    // tear down audio visualiser
    cancelAnimationFrame(audioVizRafRef.current);
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current      = null;
    audioAnalyserRef.current = null;
    setIsRecording(false);
    if (settings.hapticFeedback !== false) vibrate();
  }, [settings.hapticFeedback]);

  const handleTorch = async () => {
    const next = !torch;
    try {
      const applied = await setTorch(streamRef.current, next);
      if (applied) setTorchState(next);
    } catch {
      setError("Torch is not available on this camera.");
    }
  };

  const handleZoom = async (value) => {
    const num = Number(value);
    setShowZoomHint(true);
    clearTimeout(zoomHintTimerRef.current);
    zoomHintTimerRef.current = setTimeout(() => setShowZoomHint(false), 1400);
    if (support.zoom) {
      setZoomState(num);
      zoomStateRef.current = num;
      // Beyond hardware max: clamp hardware + layer digital zoom so slider reaches 10x
      const hwMax  = support.zoom.max || 10;
      const extra  = num > hwMax ? num / hwMax : 1;
      digitalZoomRef.current = extra;
      setDigitalZoom(extra);
      try { await setZoom(streamRef.current, Math.min(num, hwMax)); }
      catch { setError("Hardware zoom unavailable - using digital zoom."); }
    } else {
      const clamped = Math.max(1, Math.min(10, num));
      digitalZoomRef.current = clamped;
      setDigitalZoom(clamped);
    }
  };

  const handleFocus = async (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    setFocusPoint({ x: px, y: py });
    await tapToFocus(streamRef.current, { x, y });
    setTimeout(() => setFocusPoint(null), 900);
  };

  // â"€â"€ pro controls â"€â"€
  const proControlGroups = useMemo(() => {
    const setMode = (mode) => { setCameraMode(mode); showNotice(`${mode} mode ready`); };
    const limited = (feature) => showNotice(`${feature} is prepared as a web/PWA workflow. Native camera hardware support depends on the device browser.`);
    return [
      {
        title: "Capture",
        actions: [
          { label: "Capture", icon: CircleDot, active: status === "capturing", onClick: runCapture },
          { label: "Flip", icon: FlipHorizontal2, onClick: () => setFacingMode((v) => (v === "environment" ? "user" : "environment")) },
          { label: "Torch", icon: torch ? Zap : ZapOff, active: torch, onClick: handleTorch },
          { label: "Timer Off", icon: Timer, active: timer === 0, onClick: () => setTimer(0) },
          { label: "Timer 3s", icon: Clock3, active: timer === 3, onClick: () => setTimer(3) },
          { label: "Timer 5s", icon: Clock3, active: timer === 5, onClick: () => setTimer(5) },
          { label: "Timer 10s", icon: Clock3, active: timer === 10, onClick: () => setTimer(10) },
          { label: "Burst", icon: Layers, active: burst, onClick: () => { setBurst((v) => !v); limited("Burst capture"); } },
          { label: "Live", icon: Sparkles, active: livePhoto, onClick: () => { setLivePhoto((v) => !v); limited("Live photo"); } },
          { label: "HDR", icon: SunMedium, active: hdr, onClick: () => { setHdr((v) => !v); limited("HDR merge"); } },
          { label: "RAW", icon: Braces, active: rawIntent, onClick: () => { setRawIntent((v) => !v); limited("RAW capture"); } },
          { label: "JPG", icon: Image, active: shootingFormat === "JPG", onClick: () => setShootingFormat("JPG") },
          { label: "PNG", icon: ImagePlus, active: shootingFormat === "PNG", onClick: () => setShootingFormat("PNG") }
        ]
      },
      {
        title: "View",
        actions: [
          { label: "Grid", icon: Grid3X3, active: grid, onClick: () => setGrid((v) => !v) },
          { label: "Anti-Shake", icon: Activity, active: antiShake, onClick: () => setAntiShake((v) => !v) },
          { label: "Mirror", icon: FlipHorizontal2, active: mirror, onClick: () => setMirror((v) => !v) },
          { label: "Minimal", icon: Eye, active: minimalUi, onClick: () => setMinimalUi((v) => !v) },
          { label: "One Hand", icon: Maximize2, active: oneHand, onClick: () => setOneHand((v) => !v) },
          { label: "Histogram", icon: Gauge, active: histogram, onClick: () => { setHistogram((v) => !v); limited("Histogram"); } },
          { label: "Zebra", icon: BadgeCheck, active: zebra, onClick: () => { setZebra((v) => !v); limited("Zebra exposure"); } },
          { label: "Peaking", icon: Focus, active: focusPeaking, onClick: () => { setFocusPeaking((v) => !v); limited("Focus peaking"); } },
          { label: "Focus Lock", icon: Lock, active: focusLock, onClick: () => { setFocusLock((v) => !v); limited("Focus lock"); } },
          { label: "AE Lock", icon: Lock, active: exposureLock, onClick: () => { setExposureLock((v) => !v); limited("Exposure lock"); } },
          { label: "WB Lock", icon: Lock, active: whiteBalanceLock, onClick: () => { setWhiteBalanceLock((v) => !v); limited("White balance lock"); } },
          { label: "Full", icon: Maximize2, active: ratio === "Full", onClick: () => setRatio("Full") },
          { label: "1:1", icon: Crop, active: ratio === "1:1", onClick: () => setRatio("1:1") },
          { label: "4:5", icon: Crop, active: ratio === "4:5", onClick: () => setRatio("4:5") },
          { label: "9:16", icon: Crop, active: ratio === "9:16", onClick: () => setRatio("9:16") },
          { label: "16:9", icon: Crop, active: ratio === "16:9", onClick: () => setRatio("16:9") }
        ]
      },
      {
        title: "Modes",
        actions: [
          { label: "Auto",      icon: Camera,      active: cameraMode === "Auto",      onClick: () => { setMode("Auto");      setFilterId(settings.defaultFilter || "clean-pro"); } },
          { label: "Portrait",  icon: Sparkles,    active: cameraMode === "Portrait",  onClick: () => { setMode("Portrait");  setFilterId("portrait-glow"); } },
          { label: "Product",   icon: Package,     active: cameraMode === "Product",   onClick: () => { setMode("Product");   setFilterId("product-white"); } },
          { label: "Document",  icon: FileText,    active: cameraMode === "Document",  onClick: () => { setMode("Document");  setFilterId("document-sharp"); } },
          { label: "Receipt",   icon: ReceiptText, active: cameraMode === "Receipt",   onClick: () => { setMode("Receipt");   setFilterId(settings.defaultFilter || "clean-pro"); } },
          { label: "Food",      icon: Sparkles,    active: cameraMode === "Food",      onClick: () => { setMode("Food");      setFilterId("food-warm"); } },
          { label: "Travel",    icon: MapPinned,   active: cameraMode === "Travel",    onClick: () => { setMode("Travel");    setFilterId("travel-vivid"); } },
          { label: "Night",     icon: Moon,        active: cameraMode === "Night",     onClick: () => { setMode("Night");     setFilterId("night-bright"); } },
          { label: "Profile",   icon: BadgeCheck,  active: cameraMode === "Profile",   onClick: () => { setMode("Profile");   setFilterId("linkedin-studio"); } },
          { label: "Social",    icon: Captions,    active: cameraMode === "Social",    onClick: () => { setMode("Social");    setFilterId(settings.defaultFilter || "clean-pro"); } },
          { label: "Whiteboard",icon: PenLine,     active: cameraMode === "Whiteboard",onClick: () => { setMode("Whiteboard");setFilterId("document-sharp"); } },
          { label: "Macro",     icon: Focus,       active: cameraMode === "Macro",     onClick: () => { setMode("Macro");     setFilterId(settings.defaultFilter || "clean-pro"); limited("Macro focus"); } },
          { label: "Cinematic", icon: Aperture,    active: cameraMode === "Cinematic", onClick: () => { setMode("Cinematic"); setFilterId("cinematic-gold"); } }
        ]
      },
      {
        title: "AI",
        actions: [
          { label: "Assist", icon: Bot, active: aiAssistOn, onClick: () => { setAiAssistOn((v) => !v); setShowFilters(false); } },
          { label: "Scan Now", icon: Wand2, active: aiAssistLoading, onClick: analyzeFrame },
          { label: "Describe", icon: Captions, onClick: () => onNavigate("ai") },
          { label: "Quality", icon: BadgeCheck, onClick: () => onNavigate("ai") },
          { label: "Caption", icon: Type, onClick: () => onNavigate("ai") },
          { label: "Hashtags", icon: Hash, onClick: () => onNavigate("ai") },
          { label: "Coach", icon: Sparkles, onClick: () => onNavigate("ai") },
          { label: "Retake", icon: RotateCw, onClick: () => onNavigate("ai") }
        ]
      },
      {
        title: "Scanner",
        actions: [
          { label: "Scanner", icon: ScanLine, onClick: () => onNavigate("scanner") },
          { label: "QR", icon: QrCode, onClick: () => onNavigate("scanner") },
          { label: "Barcode", icon: Barcode, onClick: () => onNavigate("scanner") },
          { label: "Object", icon: Search, onClick: () => onNavigate("scanner") },
          { label: "Product", icon: Package, onClick: () => onNavigate("scanner") },
          { label: "Receipt", icon: ReceiptText, onClick: () => onNavigate("scanner") },
          { label: "Document", icon: FileText, onClick: () => onNavigate("scanner") },
          { label: "Label", icon: Tag, onClick: () => onNavigate("scanner") },
          { label: "Shopping", icon: ShoppingCart, onClick: () => onNavigate("scanner") }
        ]
      },
      {
        title: "Studio & Export",
        actions: [
          { label: "Filters", icon: Sparkles, active: showFilters, onClick: () => { setShowFilters((v) => !v); setAiAssistOn(false); } },
          { label: "Studio", icon: SlidersHorizontal, onClick: () => onNavigate("studio") },
          { label: "Gallery", icon: Folder, onClick: () => onNavigate("gallery") },
          { label: "AI Tools", icon: Bot, onClick: () => onNavigate("ai") },
          { label: "Share", icon: Share2, onClick: () => showNotice("Share opens after capture from Studio or Gallery.") },
          { label: "Download", icon: Download, onClick: () => showNotice("Export is available after capture in Studio or Gallery.") },
          { label: "Watermark", icon: Type, active: watermark, onClick: () => setWatermark((v) => !v) },
          { label: "Favorite", icon: Heart, onClick: () => showNotice("Favorite is available from Gallery history.") },
          { label: "Tags", icon: Tag, onClick: () => showNotice("Tags are saved from Gallery metadata.") },
          { label: "Sound", icon: Volume2, active: settings.soundEffects, onClick: () => showNotice("Button sound can be toggled in Settings.") },
          { label: "Settings", icon: Settings, onClick: () => onNavigate("settings") }
        ]
      }
    ];
  }, [
    aiAssistLoading, aiAssistOn, analyzeFrame, antiShake, burst, cameraMode, exposureLock, facingMode,
    focusLock, focusPeaking, grid, handleTorch, hdr, histogram, livePhoto,
    minimalUi, mirror, onNavigate, ratio, rawIntent, runCapture, settings.soundEffects,
    shootingFormat, showFilters, showNotice, status, timer, torch, watermark, whiteBalanceLock, zebra
  ]);

  // â"€â"€â"€ render â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

  return (
    <section
      className="camera-screen"
      aria-label="LensPro camera"
      onTouchStart={(e) => {
        if (e.touches.length !== 1) return;
        swipeTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: true };
      }}
      onTouchEnd={(e) => {
        if (!swipeTouchRef.current.active) return;
        swipeTouchRef.current.active = false;
        const startY = swipeTouchRef.current.y;
        // Ignore swipes that start inside the bottom controls area
        // (mode strip, zoom pills, shutter row — roughly bottom 320 px)
        const bottomControlsTop = window.innerHeight - 320;
        if (startY > bottomControlsTop) return;
        const dx = e.changedTouches[0].clientX - swipeTouchRef.current.x;
        const dy = Math.abs(e.changedTouches[0].clientY - swipeTouchRef.current.y);
        // Horizontal swipe on viewfinder → switch Photo ↔ Video
        // (disabled when filter panel is open so the carousel can be scrolled freely)
        if (Math.abs(dx) > 65 && dy < 45 && !showFilters) {
          if (dx < 0 && shootMode === "photo") {
            setShootMode("video"); setVideoMode("Normal");
            setModeHint({ label: "VIDEO", dir: "left" });
            setTimeout(() => setModeHint(null), 1100);
          } else if (dx > 0 && shootMode === "video") {
            setShootMode("photo");
            setModeHint({ label: "PHOTO", dir: "right" });
            setTimeout(() => setModeHint(null), 1100);
          }
        }
      }}
    >
      {/* Live viewfinder */}
      <video
        ref={videoRef}
        className="camera-video"
        style={{ filter: isRecording ? "none" : liveFilter, transform: videoTransform }}
        playsInline
        muted
        autoPlay
        onClick={handleFocus}
      />

      {/* Vignette scrim */}
      <div className="camera-scrim" />

      {/* Capture flash */}
      {captureFlash && <div className="capture-flash" aria-hidden="true" />}

      {/* Overlays */}
      {grid && <GridOverlay type={gridType} />}

      {/* Stabilisation badge — auto-shows when camera is interactive */}
      {cameraInteractive && steadyState !== "idle" && shootMode === "photo" && (
        <div className={`steady-badge steady-badge--${steadyState}`} aria-live="polite">
          <span className="steady-dot" />
          {steadyState === "steady" ? "STEADY" : "HOLD STILL"}
        </div>
      )}

      {/* ── Enterprise HUD ─────────────────────────────────────────────── */}
      {cameraInteractive && !minimalUi && (
        <>
          {/* Active project badge */}
          {activeProject && (
            <div className="ent-project-badge" style={{ "--proj-color": activeProject.color }}>
              <span className="ent-proj-dot" style={{ background: activeProject.color }} />
              <span className="ent-proj-name">{activeProject.name}</span>
            </div>
          )}

          {/* Active checklist progress HUD */}
          {activeChecklist && (() => {
            const done  = activeChecklist.items.filter((i) => i.captured).length;
            const total = activeChecklist.items.length;
            const next  = activeChecklist.items.find((i) => !i.captured);
            const pct   = total ? (done / total) * 100 : 0;
            return (
              <div className="ent-checklist-hud">
                <div className="ent-chk-top">
                  <span className="ent-chk-name">{activeChecklist.name}</span>
                  <span className="ent-chk-count">{done}/{total}</span>
                </div>
                <div className="ent-chk-bar">
                  <div className="ent-chk-fill" style={{ width: `${pct}%` }} />
                </div>
                {next && <span className="ent-chk-next">Next: {next.label}</span>}
              </div>
            );
          })()}

          {/* Offline / sync pending indicator */}
          {syncPending > 0 && (
            <div className="ent-sync-dot" title={`${syncPending} photo${syncPending !== 1 ? "s" : ""} queued for sync`}>
              <span className="ent-sync-dot-inner" />
              {syncPending}
            </div>
          )}
        </>
      )}

      {ratio !== "Full" && (
        <div className={`ratio-guide ratio-${ratio.replace(":", "-")}`}>
          <span className="rg-corner rg-tl" aria-hidden="true" />
          <span className="rg-corner rg-tr" aria-hidden="true" />
          <span className="rg-corner rg-bl" aria-hidden="true" />
          <span className="rg-corner rg-br" aria-hidden="true" />
          <span className="rg-label">{ratio}</span>
        </div>
      )}
      {histogram && (
        <div className="histogram-widget">
          <canvas ref={histCanvasRef} className="hist-canvas" width={176} height={62} />
          <div className="hist-channels">
            <span className="hist-luma">L</span>
            <span className="hist-r">R</span>
            <span className="hist-g">G</span>
            <span className="hist-b">B</span>
          </div>
        </div>
      )}
      {/* Anti-shake ring + meter */}
      {antiShake && cameraInteractive && (() => {
        const cls = shakeLevel < 18 ? "stable" : shakeLevel < 50 ? "moderate" : "shaky";
        return (
          <>
            <div className={`shake-ring shake-ring--${cls}`} aria-hidden="true" />
            <div className={`shake-meter shake-meter--${cls}`} aria-live="polite">
              <span className="shake-dot" aria-hidden="true" />
              <div className="shake-track">
                <div className="shake-fill" style={{ width: `${Math.min(100, shakeLevel)}%` }} />
              </div>
              <span className="shake-label">{shakeLevel < 18 ? "STABLE" : shakeLevel < 50 ? "MOVING" : "SHAKY"}</span>
            </div>
          </>
        );
      })()}
      {zebra && <div className="zebra-overlay" />}
      {focusPeaking && <div className="peaking-overlay" />}

      {/* â"€â"€ Motion detector canvas + HUD â"€â"€ */}
      <MotionDetector videoRef={videoRef} active={cameraInteractive && motionDetect} />

      {/* Square-bracket focus ring */}
      {focusPoint && (
        <span className="focus-ring-sq" style={{ left: focusPoint.x, top: focusPoint.y }}>
          <span />
        </span>
      )}


      {countdown > 0 && <div className="countdown">{countdown}</div>}

      {/* Zoom level indicator */}
      {showZoomHint && (
        <div className="zoom-indicator" aria-live="polite">
          {support.zoom ? `${zoom.toFixed(1)}x` : `${digitalZoom.toFixed(1)}x`}
        </div>
      )}

      {/* EV popup (vertical slider) */}
      {showEv && cameraInteractive && (
        <div className="ev-popup" role="dialog" aria-label="Exposure compensation">
          <span className="ev-value">{ev > 0 ? `+${ev.toFixed(1)}` : ev.toFixed(1)}</span>
          <input
            type="range"
            min={-2} max={2} step={0.1}
            value={ev}
            onChange={(e) => setEv(Number(e.target.value))}
            aria-label="Exposure compensation"
          />
          <span className="ev-label">EV</span>
        </div>
      )}

      {/* Top bar */}
      <header className="camera-top safe-top">
        <button className="icon-button glass" type="button" onClick={() => onNavigate("settings")} title="Settings">
          <Settings size={21} />
        </button>
        {isRecording ? (
          <div className="recording-badge" aria-live="assertive">
            <span className="rec-dot" />
            <span className="rec-timer">{formatDuration(recordingDuration)}</span>
            <span className="rec-label">REC</span>
          </div>
        ) : (
          <div className="brand-lockup">
            <Aperture size={20} />
            <span>LensPro AI</span>
          </div>
        )}
        <div className="camera-top-right">
          {/* EV button — disabled when locked */}
          <button
            className={`ev-btn ${showEv ? "active" : ""} ${evLocked ? "ev-locked" : ""}`}
            type="button"
            onClick={() => {
              if (evLocked) { showNotice("EV locked · tap the lock icon to unlock"); return; }
              setShowEv((v) => !v);
            }}
            title="Exposure compensation"
          >
            {evLocked ? <Lock size={14} /> : <SunMedium size={14} />}
            {ev > 0 ? `+${ev.toFixed(1)}` : ev.toFixed(1)}
          </button>
          {/* EV lock toggle */}
          <button
            className={`ev-lock-btn ${evLocked ? "active" : ""}`}
            type="button"
            onClick={() => {
              setEvLocked((v) => !v);
              setShowEv(false);
              showNotice(evLocked ? "EV unlocked" : `EV locked at ${ev > 0 ? "+" : ""}${ev.toFixed(1)}`);
            }}
            title={evLocked ? "Unlock EV" : "Lock current EV"}
          >
            <Lock size={15} />
          </button>
          {/* Torch */}
          <button className="icon-button glass" type="button" onClick={handleTorch} title="Torch">
            {torch ? <Zap size={21} /> : <ZapOff size={21} />}
          </button>
        </div>
      </header>

      {/* Side tool rail */}
      {cameraInteractive && !minimalUi && !showProControls && (
        <aside className="tool-rail" aria-label="Quick camera tools">
          <button className={`rail-button ${showFilters ? "is-active" : ""}`} type="button" onClick={() => { setShowFilters((v) => !v); setAiAssistOn(false); }}><Sparkles size={20} /><span>Filters</span></button>
          <button className={`rail-button ${aiAssistOn ? "is-active" : ""}`} type="button" onClick={() => { setAiAssistOn((v) => !v); setShowFilters(false); }}><Bot size={20} /><span>Assist</span></button>
          <button
            className={`rail-button rail-btn-motion${motionDetect ? " is-active" : ""}`}
            type="button"
            onClick={() => {
              setMotionDetect((v) => !v);
              showNotice(motionDetect ? "Motion Detect off" : "Motion Detect on - detecting moving objects");
            }}
            title="Motion Detection"
          >
            <Crosshair size={20} />
            <span>Motion</span>
          </button>
          <button className="rail-button" type="button" onClick={() => onNavigate("scanner")}><ScanLine size={20} /><span>Scanner</span></button>
          <button
            className={`rail-button ${grid ? "is-active" : ""}`}
            type="button"
            onClick={() => {
              if (!grid) {
                setGrid(true);
                setGridType("thirds");
                showNotice("Grid · Rule of Thirds");
              } else {
                const idx = GRID_TYPES.findIndex((t) => t.id === gridType);
                const next = GRID_TYPES[idx + 1];
                if (next) {
                  setGridType(next.id);
                  showNotice(next.notice);
                } else {
                  setGrid(false);
                  showNotice("Grid off");
                }
              }
            }}
          >
            <Grid3X3 size={20} /><span>Grid</span>
          </button>
          <button
            className={`rail-button ${colorPaletteOn ? "is-active" : ""}`}
            type="button"
            onClick={() => {
              setColorPaletteOn((v) => !v);
              showNotice(colorPaletteOn ? "Colour palette off" : "Colour palette on");
            }}
            title="Live colour palette"
          >
            <Sparkles size={20} /><span>Palette</span>
          </button>
          {colorPaletteOn && paletteColors.length === 3 && (
            <div className="palette-widget" aria-label="Live colour palette">
              {paletteColors.map((c, i) => (
                <span key={i} className="palette-swatch" style={{ background: c }} title={c} />
              ))}
            </div>
          )}
        </aside>
      )}

      {/* â"€â"€ Vertical zoom slider - middle-left â"€â"€ */}
      {cameraInteractive && !minimalUi && (
        <div className={`vzoom-wrap${showFilters || aiAssistOn ? " filters-open" : ""}`} aria-label="Zoom control">
          {/* Header: icon + live readout */}
          <div className="vzoom-header">
            <Search size={11} className="vzoom-icon" />
            <span className="vzoom-readout">
              {(support.zoom ? zoom : digitalZoom).toFixed(1)}
              <em className="vzoom-x">x</em>
            </span>
          </div>

          <div className="vzoom-divider" />

          <VZoomSlider
            value={support.zoom ? zoom : digitalZoom}
            min={support.zoom?.min ?? 1}
            max={Math.max(support.zoom?.max ?? 10, 10)}
            onChange={handleZoom}
          />

          <div className="vzoom-divider" />

          {/* Footer label */}
          <span className="vzoom-footer">ZOOM</span>
        </div>
      )}

      {/* Status bar */}
      <div className="camera-status">
        <span className={online ? "online" : "offline"}>
          <span className={`status-dot ${online ? "online" : "offline"}`} />
          {online ? "Online AI ready" : "Offline camera"}
        </span>
        <span>{cameraMode}</span>
        <span>{ratio}</span>
        {status === "capturing" && <span><Loader2 className="spin" size={14} /> Saving</span>}
        {aiAssistOn && <span className="online">{aiAssistLoading ? <Loader2 className="spin" size={14} /> : <Bot size={14} />} AI Assist</span>}
        {ev !== 0 && <span className={ev > 0 ? "online" : ""}><SunMedium size={13} /> {ev > 0 ? `+${ev.toFixed(1)}` : ev.toFixed(1)} EV</span>}
      </div>

      {/* AI Assist panel - hyper-premium transparent overlay */}
      {cameraInteractive && aiAssistOn && (
        <div className="ai-panel" aria-live="polite" role="complementary" aria-label="AI Assist">
          {/* Scan sweep line - shown while loading */}
          {aiAssistLoading && <div className="ai-panel-scanline" aria-hidden="true" />}

          {/* â"€â"€ Header â"€â"€ */}
          <div className="ai-panel-header">
            <div className="ai-panel-title">
              <span className="ai-panel-dot" aria-hidden="true" />
              <strong>AI Assist</strong>
              {aiAssistLoading && <Loader2 className="spin" size={11} />}
            </div>
            <div className="ai-panel-header-actions">
              {/* Auto-capture toggle */}
              <button
                type="button"
                className={`ai-panel-chip ${aiAutoCapture ? "is-active" : ""}`}
                onClick={() => {
                  setAiAutoCapture((v) => !v);
                  showNotice(aiAutoCapture ? "Auto-capture off" : "Auto-capture on - shoots when scene is ready");
                }}
                title="Auto-capture when AI says the moment is perfect"
              >
                <Focus size={10} />
                Auto
              </button>
              {/* Re-scan */}
              <button
                type="button"
                className="ai-panel-chip"
                onClick={analyzeFrame}
                disabled={aiAssistLoading}
                title="Re-analyze scene"
              >
                {aiAssistLoading ? <Loader2 className="spin" size={10} /> : <Sparkles size={10} />}
                Scan
              </button>
              {/* Close */}
              <button
                type="button"
                className="ai-panel-close"
                onClick={() => setAiAssistOn(false)}
                aria-label="Close AI Assist"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {/* â"€â"€ Error â"€â"€ */}
          {aiAssistError ? (
            <p className="ai-panel-error">{aiAssistError}</p>
          ) : aiAssistResult ? (
            <>
              {/* Scene: subject + mood + lighting type */}
              {(aiAssistResult.subject || aiAssistResult.mood || aiAssistResult.lightingType) && (
                <div className="ai-panel-scene">
                  {aiAssistResult.subject && (
                    <span className="ai-panel-subject">{aiAssistResult.subject}</span>
                  )}
                  {aiAssistResult.mood && (
                    <span className="ai-panel-tag">{aiAssistResult.mood}</span>
                  )}
              {aiAssistResult.lightingType && (
                    <span className="ai-panel-tag">{aiAssistResult.lightingType}</span>
                  )}
                </div>
              )}

              {/* Shoot now banner */}
              {aiAssistResult.shootNow && (
                <div className="ai-panel-shoot-now">
                  <Zap size={11} />
                  <strong>Shoot Now</strong>
                  <span>Perfect moment detected</span>
                </div>
              )}

              {/* Confidence bar */}
              {aiAssistResult.confidence !== null && (
                <div className="ai-panel-confidence">
                  <div className="ai-panel-conf-bar">
                    <div className="ai-panel-conf-fill" style={{ width: `${aiAssistResult.confidence}%` }} />
                  </div>
                  <span>{aiAssistResult.confidence}% ready</span>
                </div>
              )}

              {/* Composition + Lighting scores */}
              {(aiAssistResult.compositionScore !== null || aiAssistResult.lightingScore !== null) && (
                <div className="ai-panel-scores">
                  {aiAssistResult.compositionScore !== null && (
                    <div className="ai-panel-score-item">
                      <span>Compose</span>
                      <div className="ai-panel-score-bar">
                        <div style={{ width: `${aiAssistResult.compositionScore}%` }} />
                      </div>
                      <strong>{aiAssistResult.compositionScore}</strong>
                    </div>
                  )}
                  {aiAssistResult.lightingScore !== null && (
                    <div className="ai-panel-score-item">
                      <span>Lighting</span>
                      <div className="ai-panel-score-bar">
                        <div style={{ width: `${aiAssistResult.lightingScore}%` }} />
                      </div>
                      <strong>{aiAssistResult.lightingScore}</strong>
                    </div>
                  )}
                </div>
              )}

              {/* Scene labels */}
              {aiAssistResult.labels.length > 0 && (
                <div className="ai-panel-labels">
                  {aiAssistResult.labels.map((label) => (
                    <span key={label} className="ai-panel-label">{label}</span>
                  ))}
                </div>
              )}

              {/* Advice */}
              {(aiAssistResult.advice || aiAssistResult.summary) && (
                <p className="ai-panel-advice">{aiAssistResult.advice || aiAssistResult.summary}</p>
              )}

              {/* Exposure note */}
              {aiAssistResult.exposureNote && (
                <div className="ai-panel-suggestion">
                  <span>Exposure</span>
                  <span className="ai-panel-note">{aiAssistResult.exposureNote}</span>
                </div>
              )}

              {/* Filter suggestion - tappable to apply */}
              {aiAssistResult.filter && (
                <div className="ai-panel-suggestion">
                  <span>Filter</span>
                  <button
                    type="button"
                    className="ai-panel-apply-btn"
                    onClick={() => {
                      const fid = matchFilterByName(aiAssistResult.filter);
                      if (fid) { setFilterId(fid); showNotice(`Filter · ${aiAssistResult.filter}`); }
                    }}
                  >
                    <Sparkles size={10} />
                    {aiAssistResult.filter}
                  </button>
                </div>
              )}

              {/* Camera mode suggestion - tappable to apply */}
              {aiAssistResult.recommendedMode && aiAssistResult.recommendedMode !== "Auto" && (
                <div className="ai-panel-suggestion">
                  <span>Mode</span>
                  <button
                    type="button"
                    className="ai-panel-apply-btn"
                    onClick={() => {
                      const mode = aiAssistResult.recommendedMode;
                      const modeFilterMap = {
                        Portrait: "portrait-glow", Food: "food-warm", Travel: "travel-vivid",
                        Night: "night-bright", Document: "document-sharp", Product: "product-white",
                      };
                      setCameraMode(mode);
                      if (modeFilterMap[mode]) setFilterId(modeFilterMap[mode]);
                      showNotice(`${mode} mode ready`);
                    }}
                  >
                    <Camera size={10} />
                    {aiAssistResult.recommendedMode}
                  </button>
                </div>
              )}

              {/* Dominant colors palette */}
              {aiAssistResult.dominantColors.length > 0 && (
                <div className="ai-panel-colors">
                  {aiAssistResult.dominantColors.map((color, i) => (
                    <span key={i} className="ai-panel-color-chip">
                      <span
                        className="ai-color-dot"
                        style={{ background: colorToCss(color) }}
                        aria-hidden="true"
                      />
                      {color}
                    </span>
                  ))}
                </div>
              )}

            </>
          ) : (
            <p className="ai-panel-idle">
              {aiAssistLoading ? "Analyzing scene..." : "Tap Scan for a full AI shot analysis."}
            </p>
          )}
        </div>
      )}

      {/* Loading / error state */}
      {(status === "loading" || status === "error") && (
        <div className="permission-panel">
          <Camera size={42} />
          <h1>LensPro AI Camera</h1>
          <p>{status === "loading" ? "Starting the camera with rear lens preference." : error}</p>
          <button className="primary-button" type="button" onClick={bootCamera}>Enable camera</button>
          <button className="ghost-button" type="button" onClick={() => { setStatus("ready"); setError("Previewing controls without camera access. Capture needs camera permission."); }}>Preview controls</button>
          <button className="ghost-button" type="button" onClick={() => onNavigate("gallery")}>Open local gallery</button>
        </div>
      )}

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
           Premium Pro Controls Sheet - tabbed redesign
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      {cameraInteractive && showProControls && (
        <div className={`pc-sheet safe-bottom${oneHand ? " one-hand" : ""}`}>

          {/* Drag handle */}
          <div className="pc-handle-bar" aria-hidden="true" />

          {/* Header */}
          <div className="pc-head">
            <div className="pc-head-left">
              <span className="pc-badge">PRO</span>
              <div className="pc-head-text">
                <strong>Camera Controls</strong>
                <span>Professional settings</span>
              </div>
            </div>
            <button className="pc-close-btn" type="button" onClick={() => setShowProControls(false)} aria-label="Close pro controls">
              <X size={16} />
            </button>
          </div>

          {/* Tab bar */}
          <div className="pc-tab-bar">
            {PC_TABS.map(({ id, label, Icon }) => (
              <button key={id} type="button"
                className={`pc-tab${proTab === id ? " pc-tab--active" : ""}`}
                onClick={() => setProTab(id)}>
                <Icon size={12} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* â"€â"€ Tab body â"€â"€ */}
          <div className="pc-body">

            {/* â"€ Capture â"€ */}
            {proTab === "Capture" && (
              <div className="pc-section">
                <p className="pc-row-label">Format</p>
                <div className="pc-pill-row">
                  {["JPG","PNG","WEBP"].map(f => (
                    <button key={f} type="button" className={`pc-pill${shootingFormat === f ? " active" : ""}`}
                      onClick={() => setShootingFormat(f)}>{f}</button>
                  ))}
                </div>

                <p className="pc-row-label">Self Timer</p>
                <div className="pc-pill-row">
                  {[["Off",0],["3s",3],["5s",5],["10s",10]].map(([l,v]) => (
                    <button key={v} type="button" className={`pc-pill${timer === v ? " active" : ""}`}
                      onClick={() => setTimer(v)}>{l}</button>
                  ))}
                </div>

                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${hdr ? " active" : ""}`}
                    onClick={() => { setHdr(v => !v); limited("HDR merge"); }}>
                    <SunMedium size={16}/><span>HDR</span></button>
                  <button type="button" className={`pc-toggle-btn${burst ? " active" : ""}`}
                    onClick={() => { setBurst(v => !v); limited("Burst capture"); }}>
                    <Layers size={16}/><span>Burst</span></button>
                  <button type="button" className={`pc-toggle-btn${livePhoto ? " active" : ""}`}
                    onClick={() => { setLivePhoto(v => !v); limited("Live photo"); }}>
                    <Sparkles size={16}/><span>Live</span></button>
                  <button type="button" className={`pc-toggle-btn${rawIntent ? " active" : ""}`}
                    onClick={() => { setRawIntent(v => !v); limited("RAW capture"); }}>
                    <Braces size={16}/><span>RAW</span></button>
                  <button type="button" className={`pc-toggle-btn${torch ? " active" : ""}`}
                    onClick={handleTorch}>
                    {torch ? <Zap size={16}/> : <ZapOff size={16}/>}<span>Flash</span></button>
                  <button type="button" className={`pc-toggle-btn${timelapse ? " active" : ""}`}
                    onClick={() => { setTimelapse(v => !v); limited("Timelapse"); }}>
                    <Clock3 size={16}/><span>Timelapse</span></button>
                  <button type="button" className="pc-toggle-btn"
                    onClick={() => setFacingMode(v => v === "environment" ? "user" : "environment")}>
                    <FlipHorizontal2 size={16}/><span>Flip</span></button>
                  <button type="button" className={`pc-toggle-btn${status === "capturing" ? " active" : ""}`}
                    onClick={runCapture}>
                    <CircleDot size={16}/><span>Capture</span></button>
                </div>
              </div>
            )}

            {/* â"€ Exposure â"€ */}
            {proTab === "Exposure" && (
              <div className="pc-section">
                <div className="pc-slider-block">
                  <div className="pc-slider-hd">
                    <div className="pc-slider-hd-left"><SunMedium size={13}/><span>Exposure (EV)</span></div>
                    <strong className="pc-slider-val">{ev > 0 ? `+${ev.toFixed(1)}` : ev.toFixed(1)} EV</strong>
                  </div>
                  <div className="pc-range-row">
                    <span className="pc-range-edge">âˆ'3</span>
                    <input type="range" className="pc-range" min="-3" max="3" step="0.5" value={ev}
                      style={{ "--pct": `${((ev + 3) / 6) * 100}%` }}
                      onChange={e => { setEv(Number(e.target.value)); setShowEv(true); setTimeout(() => setShowEv(false), 1200); }} />
                    <span className="pc-range-edge">+3</span>
                  </div>
                  <div className="pc-ev-stops">
                    {[-3,-2,-1,0,1,2,3].map(v => (
                      <button key={v} type="button" className={`pc-ev-stop${ev === v ? " active" : ""}`}
                        onClick={() => { setEv(v); setShowEv(true); setTimeout(() => setShowEv(false), 1200); }}>
                        {v === 0 ? "0" : v > 0 ? `+${v}` : v}
                      </button>
                    ))}
                  </div>
                </div>

                <p className="pc-row-label">ISO</p>
                <div className="pc-pill-row">
                  {["Auto","50","100","200","400","800","1600","3200"].map(v => (
                    <button key={v} type="button" className={`pc-pill${iso === v ? " active" : ""}`}
                      onClick={() => { setIso(v); limited("ISO control"); }}>{v}</button>
                  ))}
                </div>

                <p className="pc-row-label">Shutter Speed</p>
                <div className="pc-pill-row">
                  {["Auto","1/1000","1/500","1/250","1/125","1/60","1/30","1/15","1/8","1/4","1/2",'1"'].map(v => (
                    <button key={v} type="button" className={`pc-pill${shutterSpeed === v ? " active" : ""}`}
                      onClick={() => { setShutterSpeed(v); limited("Shutter speed"); }}>{v}</button>
                  ))}
                </div>

                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${histogram ? " active" : ""}`}
                    onClick={() => { setHistogram(v => !v); limited("Histogram"); }}>
                    <Gauge size={16}/><span>Histogram</span></button>
                  <button type="button" className={`pc-toggle-btn${zebra ? " active" : ""}`}
                    onClick={() => { setZebra(v => !v); limited("Zebra"); }}>
                    <BadgeCheck size={16}/><span>Zebra</span></button>
                  <button type="button" className={`pc-toggle-btn${exposureLock ? " active" : ""}`}
                    onClick={() => { setExposureLock(v => !v); limited("Exposure lock"); }}>
                    <Lock size={16}/><span>AE Lock</span></button>
                </div>
              </div>
            )}

            {/* â"€ Focus â"€ */}
            {proTab === "Focus" && (
              <div className="pc-section">
                <p className="pc-row-label">Focus Mode</p>
                <div className="pc-pill-row">
                  {["Auto","Continuous","Manual","Infinity"].map(m => (
                    <button key={m} type="button" className={`pc-pill${focusMode === m ? " active" : ""}`}
                      onClick={() => { setFocusMode(m); limited("Focus mode"); }}>{m}</button>
                  ))}
                </div>

                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${focusLock ? " active" : ""}`}
                    onClick={() => { setFocusLock(v => !v); limited("Focus lock"); }}>
                    <Lock size={16}/><span>AF Lock</span></button>
                  <button type="button" className={`pc-toggle-btn${focusPeaking ? " active" : ""}`}
                    onClick={() => { setFocusPeaking(v => !v); limited("Focus peaking"); }}>
                    <Focus size={16}/><span>Peaking</span></button>
                  <button type="button" className={`pc-toggle-btn${cameraMode === "Macro" ? " active" : ""}`}
                    onClick={() => { setCameraMode(m => m === "Macro" ? "Auto" : "Macro"); limited("Macro focus"); }}>
                    <Crosshair size={16}/><span>Macro</span></button>
                  <button type="button" className={`pc-toggle-btn${whiteBalanceLock ? " active" : ""}`}
                    onClick={() => { setWhiteBalanceLock(v => !v); limited("WB lock"); }}>
                    <Lock size={16}/><span>WB Lock</span></button>
                  <button type="button" className={`pc-toggle-btn${exposureLock ? " active" : ""}`}
                    onClick={() => { setExposureLock(v => !v); limited("Exposure lock"); }}>
                    <Lock size={16}/><span>AE Lock</span></button>
                </div>
              </div>
            )}

            {/* â"€ Video â"€ */}
            {proTab === "Video" && (
              <div className="pc-section">
                <p className="pc-row-label">Resolution</p>
                <div className="pc-pill-row">
                  {["4K","1080p","720p","480p"].map(r => (
                    <button key={r} type="button" className={`pc-pill${videoResolution === r ? " active" : ""}`}
                      onClick={() => { setVideoResolution(r); limited("Video resolution"); }}>{r}</button>
                  ))}
                </div>

                <p className="pc-row-label">Frame Rate</p>
                <div className="pc-pill-row">
                  {[24,30,60].map(f => (
                    <button key={f} type="button" className={`pc-pill${videoFps === f ? " active" : ""}`}
                      onClick={() => { setVideoFps(f); limited("Frame rate"); }}>{f} fps</button>
                  ))}
                </div>

                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${stabilization ? " active" : ""}`}
                    onClick={() => { setStabilization(v => !v); limited("Video stabilization"); }}>
                    <Maximize2 size={16}/><span>Stabilize</span></button>
                  <button type="button" className={`pc-toggle-btn${noiseReduction ? " active" : ""}`}
                    onClick={() => { setNoiseReduction(v => !v); limited("Noise reduction"); }}>
                    <Sparkles size={16}/><span>Noise Red.</span></button>
                  <button type="button" className={`pc-toggle-btn${timelapse ? " active" : ""}`}
                    onClick={() => { setTimelapse(v => !v); limited("Timelapse video"); }}>
                    <Clock3 size={16}/><span>Timelapse</span></button>
                  <button type="button" className={`pc-toggle-btn${isRecording ? " active" : ""}`}
                    onClick={isRecording ? stopRecording : startRecording}>
                    <Video size={16}/><span>{isRecording ? "Stop Rec" : "Record"}</span></button>
                </div>
              </div>
            )}

            {/* â"€ White Balance â"€ */}
            {proTab === "WB" && (
              <div className="pc-section">
                <p className="pc-row-label">White Balance Presets</p>
                <div className="pc-wb-grid">
                  {WB_PRESETS.map(({ id, label, kelvin, color }) => (
                    <button key={id} type="button"
                      className={`pc-wb-btn${whiteBalance === id ? " active" : ""}`}
                      onClick={() => { setWhiteBalance(id); limited("White balance"); }}>
                      <span className="pc-wb-swatch" style={{ background: color }} />
                      <span className="pc-wb-name">{label}</span>
                      {kelvin && <span className="pc-wb-k">{kelvin}K</span>}
                    </button>
                  ))}
                </div>
                <div className="pc-toggle-grid" style={{ marginTop: "10px" }}>
                  <button type="button" className={`pc-toggle-btn${whiteBalanceLock ? " active" : ""}`}
                    onClick={() => { setWhiteBalanceLock(v => !v); limited("WB lock"); }}>
                    <Lock size={16}/><span>WB Lock</span></button>
                </div>
              </div>
            )}

            {/* â"€ View â"€ */}
            {proTab === "View" && (
              <div className="pc-section">
                <p className="pc-row-label">Grid</p>
                <div className="pc-pill-row">
                  {[["Off",false,null],["Thirds",true,"thirds"],["Square",true,"square"],["Center",true,"center"],["Golden",true,"golden"]].map(([l,g,t]) => (
                    <button key={l} type="button"
                      className={`pc-pill${(g ? grid && gridType === t : !grid) ? " active" : ""}`}
                      onClick={() => { if (g) { setGrid(true); setGridType(t); } else setGrid(false); }}>{l}</button>
                  ))}
                </div>

                <p className="pc-row-label">Aspect Ratio</p>
                <div className="pc-pill-row">
                  {["Full","1:1","4:5","9:16","16:9"].map(r => (
                    <button key={r} type="button" className={`pc-pill${ratio === r ? " active" : ""}`}
                      onClick={() => setRatio(r)}>{r}</button>
                  ))}
                </div>

                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${mirror ? " active" : ""}`}
                    onClick={() => setMirror(v => !v)}>
                    <FlipHorizontal2 size={16}/><span>Mirror</span></button>
                  <button type="button" className={`pc-toggle-btn${minimalUi ? " active" : ""}`}
                    onClick={() => setMinimalUi(v => !v)}>
                    <Eye size={16}/><span>Minimal</span></button>
                  <button type="button" className={`pc-toggle-btn${oneHand ? " active" : ""}`}
                    onClick={() => setOneHand(v => !v)}>
                    <Maximize2 size={16}/><span>One Hand</span></button>
                </div>
              </div>
            )}

            {/* â"€ Modes â"€ */}
            {proTab === "Modes" && (
              <div className="pc-section">
                <div className="pc-mode-grid">
                  {[
                    { id:"Auto",       MIcon:Camera      },
                    { id:"Portrait",   MIcon:Sparkles    },
                    { id:"Product",    MIcon:Package     },
                    { id:"Document",   MIcon:FileText    },
                    { id:"Receipt",    MIcon:ReceiptText },
                    { id:"Food",       MIcon:Sparkles    },
                    { id:"Travel",     MIcon:MapPinned   },
                    { id:"Night",      MIcon:Moon        },
                    { id:"Profile",    MIcon:BadgeCheck  },
                    { id:"Social",     MIcon:Captions    },
                    { id:"Whiteboard", MIcon:PenLine     },
                    { id:"Macro",      MIcon:Focus       },
                    { id:"Cinematic",  MIcon:Aperture    },
                  ].map(({ id, MIcon }) => {
                    const FMAP = { Portrait:"portrait-glow", Product:"product-white", Document:"document-sharp", Food:"food-warm", Travel:"travel-vivid", Night:"night-bright", Profile:"linkedin-studio", Whiteboard:"document-sharp", Cinematic:"cinematic-gold" };
                    return (
                      <button key={id} type="button"
                        className={`pc-mode-btn${cameraMode === id ? " active" : ""}`}
                        onClick={() => { setCameraMode(id); setFilterId(FMAP[id] || settings.defaultFilter || "clean-pro"); showNotice(`${id} mode ready`); }}>
                        <MIcon size={18}/>
                        <span>{id}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* â"€ AI â"€ */}
            {proTab === "AI" && (
              <div className="pc-section">
                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${aiAssistOn ? " active" : ""}`}
                    onClick={() => { setAiAssistOn(v => !v); setShowFilters(false); }}>
                    <Bot size={16}/><span>AI Assist</span></button>
                  <button type="button" className={`pc-toggle-btn${aiAutoCapture ? " active" : ""}`}
                    onClick={() => setAiAutoCapture(v => !v)}>
                    <Sparkles size={16}/><span>Auto Shoot</span></button>
                  <button type="button" className={`pc-toggle-btn${aiAssistLoading ? " active" : ""}`}
                    onClick={analyzeFrame}>
                    <Wand2 size={16}/><span>Scan Now</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("ai")}>
                    <Bot size={16}/><span>AI Tools</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("ai")}>
                    <Hash size={16}/><span>Hashtags</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("ai")}>
                    <Captions size={16}/><span>Caption</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("ai")}>
                    <Type size={16}/><span>Describe</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("ai")}>
                    <RotateCw size={16}/><span>Retake</span></button>
                </div>
              </div>
            )}

            {/* â"€ Export â"€ */}
            {proTab === "Export" && (
              <div className="pc-section">
                <div className="pc-toggle-grid">
                  <button type="button" className={`pc-toggle-btn${watermark ? " active" : ""}`}
                    onClick={() => setWatermark(v => !v)}>
                    <Type size={16}/><span>Watermark</span></button>
                  <button type="button" className={`pc-toggle-btn${showFilters ? " active" : ""}`}
                    onClick={() => { setShowFilters(v => !v); setAiAssistOn(false); }}>
                    <Sparkles size={16}/><span>Filters</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("studio")}>
                    <SlidersHorizontal size={16}/><span>Studio</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("gallery")}>
                    <Folder size={16}/><span>Gallery</span></button>
                  <button type="button" className="pc-toggle-btn" onClick={() => onNavigate("settings")}>
                    <Settings size={16}/><span>Settings</span></button>
                  <button type="button" className="pc-toggle-btn"
                    onClick={() => showNotice("Share opens from Studio or Gallery after capture.")}>
                    <Share2 size={16}/><span>Share</span></button>
                  <button type="button" className="pc-toggle-btn"
                    onClick={() => showNotice("Download is available in Gallery after capture.")}>
                    <Download size={16}/><span>Download</span></button>
                </div>
              </div>
            )}

          </div>{/* end pc-body */}
        </div>
      )}

      {/* Bottom controls */}
      {cameraInteractive && !showProControls && (
        <div className={`camera-bottom safe-bottom ${oneHand ? "one-hand" : ""}`}>
          {/* Filter carousel */}
          {showFilters && !minimalUi && (
            <FilterCarousel
              selected={filterId}
              onSelect={(id) => { setFilterId(id); setFilterAdjust({}); }}
              strength={filterStrength}
              onStrength={setFilterStrength}
              favorites={filterFavorites}
              onToggleFavorite={(id) =>
                setFilterFavorites((v) =>
                  v.includes(id) ? v.filter((x) => x !== id) : [...v, id]
                )
              }
              adjust={filterAdjust}
              onAdjust={setFilterAdjust}
            />
          )}

          {/* Zoom pills - always visible; uses hardware zoom if available, else digital */}
          <div className="zoom-pills">
            {ZOOM_PRESETS.map((z) => {
              const current = support.zoom ? zoom : digitalZoom;
              const isActive = Math.abs(current - z) < 0.28;
              return (
                <button
                  key={z}
                  type="button"
                  className={`zoom-pill ${isActive ? "is-active" : ""}`}
                  onClick={() => handleZoom(z)}
                >
                  {z}x
                </button>
              );
            })}
          </div>

          {/* Photo / Video mode switcher */}
          <div className="shoot-mode-switcher">
            <button
              type="button"
              className={`shoot-mode-btn ${shootMode === "photo" ? "is-active" : ""}`}
              onClick={() => { setShootMode("photo"); showNotice("Photo mode"); }}
            >
              Photo
            </button>
            <button
              type="button"
              className={`shoot-mode-btn video ${shootMode === "video" ? "is-active" : ""}`}
              onClick={() => { setShootMode("video"); setVideoMode("Normal"); showNotice("Video mode"); }}
            >
              <Video size={12} /> Video
            </button>
          </div>

          {/* Camera mode strip - photo only */}
          {shootMode === "photo" && (
            <div className="camera-mode-strip" aria-label="Camera modes">
              {CAMERA_MODES_STRIP.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`strip-mode-btn ${cameraMode === mode.id ? "is-active" : ""}`}
                  onClick={() => {
                    setCameraMode(mode.id);
                    setFilterId(mode.filter || settings.defaultFilter || "clean-pro");
                    showNotice(`${mode.label} mode ready`);
                  }}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          )}

          {/* Video mode strip - video only */}
          {shootMode === "video" && (
            <div className="camera-mode-strip" aria-label="Video modes">
              {VIDEO_MODES_STRIP.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`strip-mode-btn ${videoMode === mode.id ? "is-active" : ""}`}
                  onClick={() => {
                    setVideoMode(mode.id);
                    if (mode.filter) setFilterId(mode.filter);
                    else setFilterId(settings.defaultFilter || "clean-pro");
                    showNotice(mode.notice);
                  }}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Interval shot row — photo mode only ─────────────────────────── */}
          {shootMode === "photo" && (
            <div className="interval-row">
              <span className="interval-label">Auto Shot</span>
              {[0, 2, 5, 10, 30].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`interval-btn${intervalSecs === s ? " is-active" : ""}`}
                  onClick={() => {
                    setIntervalSecs(s);
                    showNotice(s === 0 ? "Auto Shot off" : `Auto Shot every ${s}s`);
                  }}
                >
                  {s === 0 ? "Off" : `${s}s`}
                </button>
              ))}
            </div>
          )}

          {/* Main controls row */}
          <div className="camera-controls">
            {/* Gallery button */}
            <button className="icon-button glass" type="button" onClick={() => onNavigate("gallery")} title="Gallery">
              <Folder size={22} />
            </button>

            {/* Timer + Pro */}
            <div className="timer-stack">
              <button className={`pro-mini-button ${showProControls ? "is-active" : ""}`} type="button" onClick={() => setShowProControls((v) => !v)}>
                <SlidersHorizontal size={17} /> Pro
              </button>
              <select value={timer} onChange={(e) => setTimer(Number(e.target.value))} aria-label="Timer">
                <option value={0}>Off</option>
                <option value={3}>3s</option>
                <option value={5}>5s</option>
                <option value={10}>10s</option>
              </select>
            </div>

            {/* Shutter / Record button */}
            {shootMode === "video" ? (
              <button
                className={`capture-button video-mode${isRecording ? " is-recording" : ""}${captureFiring ? " firing" : ""}`}
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={status !== "ready"}
                title={isRecording ? "Stop recording" : "Start recording"}
              >
                <span />
              </button>
            ) : (
              <button
                className={`capture-button${captureFiring ? " firing" : ""}${isBursting ? " is-bursting" : ""}`}
                type="button"
                onPointerDown={handleShutterDown}
                onPointerUp={handleShutterUp}
                onPointerCancel={handleShutterCancel}
                disabled={status !== "ready"}
                title="Capture · Hold for burst"
              >
                <span />
                {isBursting && <span className="burst-badge">{burstCount}</span>}
              </button>
            )}

            {/* Flip camera - col 4, directly beside capture */}
            <button className="flip-button" type="button" onClick={() => setFacingMode((v) => (v === "environment" ? "user" : "environment"))} title="Flip camera">
              <FlipHorizontal2 size={26} />
            </button>

            {/* Quick Ratio + Mirror - col 5 */}
            <div className="toggle-stack">
              {/* Quick Ratio — tap to cycle through all aspect ratios */}
              <button
                className={`icon-button glass text-icon-button ${ratio !== "Full" ? "is-active" : ""}`}
                type="button"
                onClick={() => {
                  const RATIOS = ["Full", "1:1", "4:5", "9:16", "16:9"];
                  const next = RATIOS[(RATIOS.indexOf(ratio) + 1) % RATIOS.length];
                  setRatio(next);
                  showNotice(`Ratio - ${next}`);
                }}
                title="Cycle aspect ratio"
              >
                {ratio}
              </button>
              <button
                className={`icon-button glass text-icon-button ${mirror ? "is-active" : ""}`}
                type="button"
                onClick={() => {
                  setMirror((v) => !v);
                  showNotice(facingMode === "user" ? (mirror ? "Mirror selfie off" : "Mirror selfie on") : "Mirror is ready for the front camera");
                }}
                title="Mirror selfie"
                aria-pressed={mirror}
              >
                Mirror
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Recording audio visualiser ── */}
      {isRecording && (
        <div className="rec-viz-wrap" aria-hidden="true">
          <span className="rec-viz-mic-dot" />
          <canvas
            ref={audioVizCanvasRef}
            className="rec-viz-canvas"
            width={260}
            height={64}
          />
        </div>
      )}

      {/* ── Swipe mode hint ── */}
      {modeHint && (
        <div className={`mode-hint mode-hint--${modeHint.dir}`} aria-live="polite">
          {modeHint.dir === "right" && <span className="mode-hint-arrow">‹</span>}
          <span className="mode-hint-label">{modeHint.label}</span>
          {modeHint.dir === "left" && <span className="mode-hint-arrow">›</span>}
        </div>
      )}

      {/* Toast */}
      {(error && status !== "error") || proNotice
        ? <p className={`toast ${cameraInteractive ? "camera-toast" : ""} ${showProControls ? "above-pro" : ""}`}>{proNotice || error}</p>
        : null}
    </section>
  );
}
